import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

// PrimeReact components
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { ProgressBar } from 'primereact/progressbar';
import { Toast } from 'primereact/toast';
import { Divider } from 'primereact/divider';
import { Tag } from 'primereact/tag';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { Checkbox } from 'primereact/checkbox';
import { Dialog } from 'primereact/dialog';
import { TabView, TabPanel } from 'primereact/tabview';

// Context and services
import { useScenarioContext, scenarioActions } from '../../context/ScenarioContext';
import ApiService, { handleApiError } from '../../services/api';

// Types
import { ProductFamily, UserProductFamilies, ScenarioRequestWithMinsAndMaxAndPvt, PayerMixItem, ScenarioFormState, ProductFamilySelection, PayerGrouping, ExcludedOpportunity } from '../../types';

// Styles
import './ScenarioGenerator.css';
import { Tooltip } from 'primereact/tooltip';
import { Slider } from 'primereact/slider';
import { AutoComplete } from 'primereact/autocomplete';
import { MultiSelect } from 'primereact/multiselect';
import { InputSwitch } from 'primereact/inputswitch';
import { usePracticeData, PRACTICE_KEY_DELIMITER, getDisplayNameFromKey, CATEGORIES_SESSION_KEY, PRODUCT_FAMILIES_SESSION_KEY, EXCLUDED_OPPORTUNITIES_SESSION_KEY } from '../../hooks/usePracticeData';
import CategorySizeControl from './CategorySizeControl';

const ScenarioGenerator: React.FC = () => {
  const { state, dispatch } = useScenarioContext();
  const navigate = useNavigate();
  const toast = useRef<Toast>(null);

  // Form state is now primarily driven by context, with local state for UI control
  const { generatorState } = state;
  const formData = useMemo(() => generatorState.form ?? {}, [generatorState.form]);

  const selectedProductFamilies = useMemo(() => generatorState.selectedFamilies ?? {}, [generatorState.selectedFamilies]);
  const userName = useMemo(() => generatorState.userName ?? null, [generatorState.userName]);
  // const useCurrentOnly = useMemo(() => generatorState.useCurrentOnly ?? false, [generatorState.useCurrentOnly]);

  // UI state
  const [availableCategories, setAvailableCategories] = useState<string[]>([]);
  const [productFamilies, setProductFamilies] = useState<ProductFamily[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState(0);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [userIdInput, setUserIdInput] = useState(formData.logged_in_user || '');
  const { allCustomers, practiceMap, corporateParentSet, orgDropdownLocked, isInternal, isDemoMode } = usePracticeData();
  const [filteredCustomers, setFilteredCustomers] = useState<string[]>([]);
  const orphanChildKeysRef = useRef<Set<string>>(new Set());
  const hasAutoSelectedOrgRef = useRef(false);
  const [drugPayerMatrix, setDrugPayerMatrix] = useState<{ [drugId: string]: { [payer: string]: boolean } }>(formData.drugPayerMatrix ?? {});
  // Per-category volume scale factor (1.0 = 100% of last quarter's actual buy).
  // Seed from the cached map so size adjustments survive navigation back to this page.
  const [categoryScaleFactors, setCategoryScaleFactors] = useState<Record<string, number>>(
    () => generatorState.form?.category_scale_factor_map ?? {}
  );
  // Last quarter's actual total category units, fetched once per account+category and
  // cached so the size control shows the real modeled unit count from the start.
  // Keyed by `${account}||${category}`.
  const [baselineUnitsByCategory, setBaselineUnitsByCategory] = useState<Record<string, number>>({});
  const [excludedOpportunities, setExcludedOpportunities] = useState<ExcludedOpportunity[]>([]);
  const [showExcludedOpportunities, setShowExcludedOpportunities] = useState(false);
  const [selectedExcludedOpportunities, setSelectedExcludedOpportunities] = useState<ExcludedOpportunity[]>(formData.selectedExcludedOpportunities ?? []);
  // Track whether user has modified payer eligibility since opening dialog
  const [matrixDirty, setMatrixDirty] = useState(false);
  const originalMatrixRef = useRef<string>('');
  const originalExcludedOpportunitiesRef = useRef<ExcludedOpportunity[]>([]);
  const [payerMixEligible, setPayerMixEligible] = useState<boolean>(false);
  const [payerMixLoaded, setPayerMixLoaded] = useState<boolean>(false);
  const hasLoadedPayerDataRef = useRef<string | null>(null);
  const [showEligibilityMatrix, setShowEligibilityMatrix] = useState(false);
  // Toggle to display each payer's effective (eligibility-normalized) share inside matrix cells
  const [showEffectiveShare, setShowEffectiveShare] = useState(false);
  // Synchronized category state for both config and preview
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [payerMixByCategory, setPayerMixByCategory] = useState<Record<string, PayerMixItem[]>>({});
  // Active category's payer mix derived from the per-category map
  const payerMix = useMemo(() => (activeCategory ? payerMixByCategory[activeCategory] ?? [] : []), [activeCategory, payerMixByCategory]);
  // Payers with 0.0% are hidden from the eligibility matrix
  const visiblePayerMix = useMemo(() => payerMix.filter((p) => p.percent > 0), [payerMix]);
  const [payerGroupingsByCategory, setPayerGroupingsByCategory] = useState<Record<string, PayerGrouping[]>>({});
  // Active category's payer groupings derived from the per-category map
  const payerGroupings = useMemo(() => (activeCategory ? payerGroupingsByCategory[activeCategory] ?? [] : []), [activeCategory, payerGroupingsByCategory]);
  const [payerGroupingsLoading, setPayerGroupingsLoading] = useState(false);
  const [showPayerGroupingsDialog, setShowPayerGroupingsDialog] = useState(false);

  // Generation mode
  // Checked = payor-based greedy generation; Unchecked = legacy generation
  const [generateByPayers, setGenerateByPayers] = useState<boolean>(false);

  // Fall back to distribution-based if payer eligibility is lost; don't auto-select payer-optimized when it becomes available.
  const prevPayerMixEligible = useRef(payerMixEligible);
  useEffect(() => {
    if (prevPayerMixEligible.current && !payerMixEligible) {
      setGenerateByPayers(false);
    }
    prevPayerMixEligible.current = payerMixEligible;
  }, [payerMixEligible]);

  // Restore the last-used generation mode (persisted in generatorState) exactly once,
  // and only after payer eligibility is confirmed. First login has no stored preference,
  // so it stays on distribution-based (the default). We never restore payer-based while
  // ineligible, and the one-shot ref ensures we don't fight the user's in-session toggles.
  const didRestoreGenerationMode = useRef(false);
  useEffect(() => {
    if (didRestoreGenerationMode.current) return;
    if (payerMixEligible && state.generatorState?.preferredGenerationMode === 'payer') {
      setGenerateByPayers(true);
      didRestoreGenerationMode.current = true;
    }
  }, [payerMixEligible, state.generatorState?.preferredGenerationMode]);

  const payerGroupingsRowsPerPage = 10;
  const [payerDialogActiveTabIndex, setPayerDialogActiveTabIndex] = useState(0);
  const [payerGroupingFirst, setPayerGroupingFirst] = useState(0);
  const [payerGroupingHighlight, setPayerGroupingHighlight] = useState<string | null>(null);
  const [payerGroupingScrollId, setPayerGroupingScrollId] = useState<string | null>(null);

  // When the active category changes, reset the payer dialog to its default
  // view: the Eligibility Matrix tab and page 1 of the groupings table. A
  // stale page index (e.g. page 3 from a larger category) would break the
  // paginator if the newly selected category has fewer rows.
  useEffect(() => {
    setPayerDialogActiveTabIndex(0);
    setPayerGroupingFirst(0);
    setPayerGroupingHighlight(null);
    setPayerGroupingScrollId(null);
  }, [activeCategory]);

  // Add state to track loading status of payer preferences for each category
  const [payerPreferencesLoaded, setPayerPreferencesLoaded] = useState<Record<string, boolean>>({});

  const groupedPayerRows = useMemo(() => {
    if (!payerGroupings || payerGroupings.length === 0) return [];

    const grouped = new Map<string, string[]>();
    payerGroupings.forEach((item) => {
      const group = item.MasterPayorGroup?.trim() || 'Unknown';
      const payer = item.InsurancePayerPrimary?.trim() || 'Unknown';
      const existing = grouped.get(group) ?? [];
      if (!existing.includes(payer)) existing.push(payer);
      grouped.set(group, existing);
    });

    // Order master groups by their total payer share — same ordering the
    // eligibility matrix uses. payerMix is keyed by master payor group name,
    // so each group's share is a direct lookup (no summing across primaries).
    const shareByGroup = new Map<string, number>();
    payerMix.forEach((item) => {
      shareByGroup.set(item.payer.trim().toLowerCase(), item.percent);
    });

    const orderedGroups = Array.from(grouped.entries()).sort(([a], [b]) => {
      const shareA = shareByGroup.get(a.trim().toLowerCase()) ?? 0;
      const shareB = shareByGroup.get(b.trim().toLowerCase()) ?? 0;
      if (shareB !== shareA) return shareB - shareA; // larger share first
      return a.localeCompare(b, undefined, { sensitivity: 'base' }); // stable tie-break
    });

    const rows = orderedGroups.flatMap(([group, payers]) =>
      payers.map((payer, index) => ({
        masterPayorGroup: index === 0 ? group : '',
        masterPayorGroupFull: group,
        insurancePayerPrimary: payer,
        isGroupEnd: index === payers.length - 1,
      }))
    );

    return rows.map((r, rowIndex) => ({
      ...r,
      rowIndex,
      rowId: `payer-groupings-row-${rowIndex}`,
    }));
  }, [payerGroupings, payerMix]);

  const payersInGroupings = useMemo(() => {
    const set = new Set<string>();
    payerGroupings.forEach((item) => {
      const group = item.MasterPayorGroup?.trim().toLowerCase();
      if (group) set.add(group);
    });
    return set;
  }, [payerGroupings]);

  const jumpToPayerGrouping = (masterPayorGroupName: string) => {
    const hasGroupings = payerGroupings.length > 0;
    if (!hasGroupings) {
      toast.current?.show({
        severity: 'info',
        summary: 'No Groupings',
        detail: 'No payer groupings available for this organization.',
        life: 2500,
      });
      return;
    }

    const normalized = masterPayorGroupName.trim().toLowerCase();
    const exactIndex = groupedPayerRows.findIndex(
      (r: any) => (r.masterPayorGroupFull || '').toLowerCase() === normalized
    );
    const containsIndex = groupedPayerRows.findIndex(
      (r: any) => (r.masterPayorGroupFull || '').toLowerCase().includes(normalized)
    );
    const targetIndex = exactIndex >= 0 ? exactIndex : containsIndex;

    // Switch to the Groupings tab (index 1) regardless; it helps user orient.
    setPayerDialogActiveTabIndex(1);

    if (targetIndex < 0) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Not Found',
        detail: `Couldn't find "${masterPayorGroupName}" in master payor groups.`,
        life: 3000,
      });
      return;
    }

    const first = Math.floor(targetIndex / payerGroupingsRowsPerPage) * payerGroupingsRowsPerPage;
    setPayerGroupingFirst(first);

    const targetRow = groupedPayerRows[targetIndex] as any;
    setPayerGroupingHighlight(targetRow.masterPayorGroupFull);
    setPayerGroupingScrollId(targetRow.rowId);
  };

  useEffect(() => {
    if (!showEligibilityMatrix) return;
    if (payerDialogActiveTabIndex !== 1) return;
    if (!payerGroupingScrollId) return;

    // Wait for tab + pagination render before scrolling.
    const id = payerGroupingScrollId;
    const t = window.setTimeout(() => {
      const el = document.getElementById(id);
      el?.scrollIntoView({ block: 'center' });
    }, 50);

    return () => window.clearTimeout(t);
  }, [showEligibilityMatrix, payerDialogActiveTabIndex, payerGroupingFirst, payerGroupingScrollId]);

  // Delimiter to create unique keys to avoid issues if two corpEntities have same name

  const searchCustomer = (event: { query?: string | null}) => {
    // Trimmed for matching only -- the input keeps whatever the user typed.
    const query = (event.query ?? '').trim().toLowerCase();

    if (!query) {
      orphanChildKeysRef.current = new Set();
      setFilteredCustomers([...allCustomers]);
      return;
    }

    // If query is empty but we have a cached userName, use that to filter and bring matching item to focus
    const effectiveQuery = query || (userName ? userName.toLowerCase() : '');
    
    if (!effectiveQuery) {
      // No query and no cached userName - show all customers
      orphanChildKeysRef.current = new Set();
      setFilteredCustomers(allCustomers);
      return;
    }
    
    const filtered = allCustomers.filter((cust) => {
      // Search against the display name portion only (before delimiter)
      const displayName = getDisplayNameFromKey(cust);
      return displayName.toLowerCase().includes(effectiveQuery);
    });
    
    // Build a parent→children lookup from the original allCustomers ordering.
    // A child practice is any entry whose display name starts with leading spaces
    // and that immediately follows a corporate parent (or another child of that parent).
    const parentOfChild = new Map<string, string>(); // childKey → corpParentKey
    let currentParent: string | null = null;
    allCustomers.forEach((key) => {
      if (corporateParentSet.has(key)) {
        currentParent = key;
      } else if (currentParent && getDisplayNameFromKey(key).startsWith('    ')) {
        parentOfChild.set(key, currentParent);
      } else {
        currentParent = null; // standalone practice, reset parent tracking
      }
    });

    const filteredSet = new Set(filtered);

    // Group 1: Corporate parents that matched + their matched children, sorted alphabetically by parent
    const matchedCorps = filtered
      .filter((k) => corporateParentSet.has(k))
      .sort((a, b) => {
        const da = getDisplayNameFromKey(a).toLowerCase().trim();
        const db = getDisplayNameFromKey(b).toLowerCase().trim();
        const exactA = da === effectiveQuery;
        const exactB = db === effectiveQuery;
        if (exactA && !exactB) return -1;
        if (exactB && !exactA) return 1;
        return da.localeCompare(db);
      });

    const grouped: string[] = [];
    const usedKeys = new Set<string>();

    matchedCorps.forEach((corpKey) => {
      grouped.push(corpKey);
      usedKeys.add(corpKey);
      // Append matched children of this corp in their original order
      allCustomers.forEach((key) => {
        if (parentOfChild.get(key) === corpKey && filteredSet.has(key)) {
          grouped.push(key);
          usedKeys.add(key);
        }
      });
    });

    // Group 2: Children whose parent corp is NOT in results + standalone practices
    const orphans = filtered
      .filter((k) => !usedKeys.has(k))
      .sort((a, b) => {
        const da = getDisplayNameFromKey(a).toLowerCase().trim();
        const db = getDisplayNameFromKey(b).toLowerCase().trim();
        const exactA = da === effectiveQuery;
        const exactB = db === effectiveQuery;
        if (exactA && !exactB) return -1;
        if (exactB && !exactA) return 1;
        return da.localeCompare(db);
      });

    // Track which children appear without their parent so itemTemplate can unindent them
    const orphanChildKeys = new Set<string>();
    orphans.forEach((k) => {
      if (getDisplayNameFromKey(k).startsWith('    ')) {
        orphanChildKeys.add(k);
      }
    });
    orphanChildKeysRef.current = orphanChildKeys;

    setFilteredCustomers([...grouped, ...orphans]);
  };

  useEffect(() => {
    setUserIdInput(formData.logged_in_user || '');
  }, [formData.logged_in_user]);

  useEffect(() => {
    setFilteredCustomers([...allCustomers]);
  }, [allCustomers]);

  useEffect(() => {
    if (hasAutoSelectedOrgRef.current) return;
    if (allCustomers.length === 0 || Object.keys(practiceMap).length === 0) return;
    if (formData.logged_in_user) {
      hasAutoSelectedOrgRef.current = true;
      return;
    }
    // Auto-select when locked (single org) OR when external user has a corporate
    // parent as their first entry (default to grandparent, keep dropdown selectable).
    const shouldAutoSelect = orgDropdownLocked ||
      (!isInternal && corporateParentSet.has(allCustomers[0]));
    if (!shouldAutoSelect) return;
    const firstKey = allCustomers[0];
    const accountNumber = practiceMap[firstKey];
    if (!firstKey || !accountNumber) return;
    hasAutoSelectedOrgRef.current = true;
    const displayName = getDisplayNameFromKey(firstKey);
    dispatch(scenarioActions.setGeneratorState({
      userName: displayName.trim(),
      form: { logged_in_user: accountNumber, categories: [], max_scenarios: 25 },
      selectedFamilies: {},
    }));
    setUserIdInput(accountNumber);
  }, [orgDropdownLocked, isInternal, allCustomers, practiceMap, corporateParentSet, formData.logged_in_user]);

  // Load payer mix for a specific category
  const loadPayerMixByCategory = async (userId: string, category: string) => {
    setPayerMixLoaded(false);
    try {
      const response = await ApiService.getPayerMixByCategory({ logged_in_user: userId, category });
      if (response.mix && response.mix.length > 0) {
        setPayerMixByCategory((prev) => ({ ...prev, [category]: response.mix }));
        setPayerMixEligible(true);
        // Only load payer preferences for this category, passing the mix directly
        loadPayerPreferences(category, response.mix);
      } else {
        setPayerMixByCategory((prev) => ({ ...prev, [category]: [] }));
        setPayerMixEligible(false);
      }
    } catch (error) {
      console.error('Failed to load payer mix by category:', error);
      setPayerMixByCategory((prev) => ({ ...prev, [category]: [] }));
      setPayerMixEligible(false);
      toast.current?.show({
        severity: 'error',
        summary: 'Payer Mix Error',
        detail: 'Could not load payer mix for this category. Generation will proceed with all payers eligible.',
        life: 4500,
      });
    } finally {
      setPayerMixLoaded(true);
    }
  };

  const loadPayerGroupings = async (userId: string, category: string) => {
    setPayerGroupingsLoading(true);
    try {
      const response = await ApiService.getPayerGroupingsByCategory(userId, category);
      const raw = response.data;
      // API returns { "MasterPayorGroup": ["payer1", "payer2"], ... }
      // Transform into PayerGrouping[] for the UI
      if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
        const rows: PayerGrouping[] = [];
        for (const group of Object.keys(raw)) {
          const payers = (raw as Record<string, string[]>)[group] ?? [];
          for (const payer of payers) {
            rows.push({ MasterPayorGroup: group, InsurancePayerPrimary: payer });
          }
        }
        setPayerGroupingsByCategory((prev) => ({ ...prev, [category]: rows }));
      } else {
        setPayerGroupingsByCategory((prev) => ({ ...prev, [category]: Array.isArray(raw) ? raw : [] }));
      }
    } catch (error) {
      console.error('Failed to load payer groupings:', error);
      setPayerGroupingsByCategory((prev) => ({ ...prev, [category]: [] }));
      toast.current?.show({
        severity: 'error',
        summary: 'Payer Groupings Error',
        detail: 'Could not load payer groupings.',
        life: 4500,
      });
    } finally {
      setPayerGroupingsLoading(false);
    }
  };

  // Ensure productFamiliesByCategory is declared before effects referencing it
  const [productFamiliesByCategory, setProductFamiliesByCategory] = useState<Record<string, ProductFamily[]>>({});
  const previousCategoriesRef = useRef<string[]>([]);

  const activeCategoryFamilies = useMemo(() => {
    const families = activeCategory ? (productFamiliesByCategory[activeCategory] || []) : [];
    return [...families].sort((a, b) => {
      const nameA = String(a.product_family_name ?? '').trim();
      const nameB = String(b.product_family_name ?? '').trim();
      return nameA.localeCompare(nameB, undefined, { sensitivity: 'base' });
    });
  }, [activeCategory, productFamiliesByCategory]);

  // Last quarter's actual category units for the active category — the "Current"
  // (scenario_id 0) total shown in the preview. Feeds the category-size caption so
  // it can display the modeled unit count. Prefers the freshly fetched value, then
  // falls back to the current results in context; null when neither is available.
  const activeCategoryBaselineUnits = useMemo<number | null>(() => {
    if (!activeCategory) return null;
    const account = formData.logged_in_user;
    const key = account ? `${account}||${activeCategory}` : null;
    const fetched = key ? baselineUnitsByCategory[key] : undefined;
    if (fetched != null && fetched > 0) return fetched;
    const total = (state.scenarioResults ?? [])
      .filter((r) => Number(r.scenario_id) === 0 && r.category === activeCategory)
      .reduce((sum, r) => sum + Number(r.category_units || 0), 0);
    return total > 0 ? total : null;
  }, [activeCategory, formData.logged_in_user, baselineUnitsByCategory, state.scenarioResults]);

  // Fetch and cache last quarter's actual category units once per account+category,
  // so the modeled unit count is available before any scenario is generated.
  useEffect(() => {
    const account = formData.logged_in_user;
    if (!account || !activeCategory) return;
    const key = `${account}||${activeCategory}`;
    if (baselineUnitsByCategory[key] !== undefined) return; // already fetched
    let cancelled = false;
    (async () => {
      try {
        const units = await ApiService.getBaselineCategoryUnits(account, activeCategory);
        if (!cancelled) {
          setBaselineUnitsByCategory((prev) => ({ ...prev, [key]: units }));
        }
      } catch (err) {
        // Non-fatal: the caption falls back to the results-derived baseline or
        // the unit-less wording when the fetch fails.
        console.error('Failed to fetch baseline category units', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [formData.logged_in_user, activeCategory, baselineUnitsByCategory]);

  const productFamiliesById = useMemo(() => {
    return Object.values(productFamiliesByCategory).flat().reduce<Record<string, ProductFamily>>((acc, family) => {
      acc[String(family.product_family_id)] = family;
      return acc;
    }, {});
  }, [productFamiliesByCategory]);

  // Payer-optimized generation is unsupported for oral inventory. Disable it when the active
  // category contains a product family with InventoryType = 'Oral', so switching categories
  // toggles the option (consistent with payerMixEligible, which is also per active category).
  const hasOralInventory = useMemo(() => {
    if (!activeCategory) return false;
    return (productFamiliesByCategory[activeCategory] ?? []).some(
      (pf) => (pf.inventory_type ?? '').trim().toLowerCase() === 'oral'
    );
  }, [activeCategory, productFamiliesByCategory]);

  // Oral inventory doesn't support payer-optimized generation; force distribution-based.
  useEffect(() => {
    if (hasOralInventory) {
      setGenerateByPayers(false);
    }
  }, [hasOralInventory]);

  // Determine if there are at least 2 selected product families with some eligible payer percentage (>0)
  const hasAtLeastTwoEligibleFamiliesForActive = useMemo(() => {
    if (!activeCategory) return false;
    const families = productFamiliesByCategory[activeCategory] || [];

    let eligibleCount = 0;
    for (const pf of families) {
      const isSelected = selectedProductFamilies[pf.product_family_name]?.include ?? true;
      if (!isSelected) continue;

      const uncheckedPercent = payerMix.reduce((acc, item) => {
        const isLoaded = payerPreferencesLoaded[activeCategory];
        const isChecked = (drugPayerMatrix[pf.product_family_id]?.[item.payer] ?? (isLoaded ? true : false));
        return !isChecked ? acc + item.percent : acc;
      }, 0);
      const maxMarketShare = 100 - uncheckedPercent;
      if (maxMarketShare > 0) {
        eligibleCount += 1;
        if (eligibleCount >= 2) return true;
      }
    }
    return false;
  }, [activeCategory, productFamiliesByCategory, selectedProductFamilies, payerMix, drugPayerMatrix, payerPreferencesLoaded]);

  const isAllFamiliesSelectedInActiveCategory = useMemo(() => {
    if (!activeCategory) return false;
    const families = productFamiliesByCategory[activeCategory] || [];
    if (families.length === 0) return false;
    return families.every((pf) => selectedProductFamilies[pf.product_family_name]?.include);
  }, [activeCategory, productFamiliesByCategory, selectedProductFamilies]);

  const isAnyFamilySelectedInActiveCategory = useMemo(() => {
    if (!activeCategory) return false;
    const families = productFamiliesByCategory[activeCategory] || [];
    if (families.length === 0) return false;
    return families.some((pf) => selectedProductFamilies[pf.product_family_name]?.include);
  }, [activeCategory, productFamiliesByCategory, selectedProductFamilies]);

  const isSelectAllIndeterminate = useMemo(() => {
    return isAnyFamilySelectedInActiveCategory && !isAllFamiliesSelectedInActiveCategory;
  }, [isAnyFamilySelectedInActiveCategory, isAllFamiliesSelectedInActiveCategory]);

  const selectAllCheckboxInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!selectAllCheckboxInputRef.current) return;
    // PrimeReact Checkbox does not expose an `indeterminate` prop; use native input state.
    selectAllCheckboxInputRef.current.indeterminate = isSelectAllIndeterminate;
  }, [isSelectAllIndeterminate]);

  const toggleSelectAllFamiliesInActiveCategory = () => {
    if (!activeCategory) return;
    const families = productFamiliesByCategory[activeCategory] || [];
    if (families.length === 0) return;

    const shouldSelectAll = !families.every((pf) => selectedProductFamilies[pf.product_family_name]?.include);
    const updated: Record<string, ProductFamilySelection> = { ...selectedProductFamilies };

    families.forEach((pf) => {
      const existing = updated[pf.product_family_name] || { include: false, minDistribution: 0 };
      updated[pf.product_family_name] = {
        ...existing,
        include: shouldSelectAll,
        minDistribution: existing.minDistribution ?? 0,
      };
    });

    dispatch(scenarioActions.setGeneratorState({ selectedFamilies: updated }));
  };

  // Initialize selections once product families are loaded per category.
  // Include only those with current_distribution > 0; all min distributions start at 0.
  useEffect(() => {
    const allFamilies = Object.values(productFamiliesByCategory).flat();
    if (allFamilies.length === 0) return;

    // Build or extend selection map without overwriting user changes.
    let changed = false;
    const updated: Record<string, ProductFamilySelection> = { ...selectedProductFamilies };
    allFamilies.forEach(pf => {
      if (!updated[pf.product_family_name]) {
        updated[pf.product_family_name] = {
          include: pf.current_distribution > 0,
          minDistribution: 0,
        };
        changed = true;
      } else {
        // If we previously auto-added and never interacted (minDistribution still undefined)
        if (updated[pf.product_family_name].minDistribution === undefined) {
          updated[pf.product_family_name].minDistribution = 0;
          changed = true;
        }
      }
    });
    if (changed) {
      dispatch(scenarioActions.setGeneratorState({ selectedFamilies: updated }));
    }
  }, [productFamiliesByCategory]);



  useEffect(() => {
    if (formData.logged_in_user) {
      loadUserCategories();
      loadExcludedOpportunities(formData.logged_in_user);
    }
  }, [formData.logged_in_user]);

  // Pre-fill org name when logged_in_user is set (e.g. from Account Settings) but userName is empty
  useEffect(() => {
    if (formData.logged_in_user && !userName && Object.keys(practiceMap).length > 0) {
      const matchingKey = Object.entries(practiceMap).find(
        ([, acctNum]) => acctNum === formData.logged_in_user
      );
      if (matchingKey) {
        const displayName = getDisplayNameFromKey(matchingKey[0]);
        dispatch(scenarioActions.setGeneratorState({ userName: displayName }));
        setUserIdInput(formData.logged_in_user);
      }
    }
  }, [formData.logged_in_user, userName, practiceMap]);


  useEffect(() => {
    if (formData.logged_in_user && formData.categories && formData.categories.length > 0) {
      const currentCategories = formData.categories;
      const previousCategories = previousCategoriesRef.current;

      // Find deselected categories
      const deselectedCategories = previousCategories.filter(cat => !currentCategories.includes(cat));

      if (deselectedCategories.length > 0) {
        // Clean up selections and product families for deselected categories
        const updatedSelections = { ...selectedProductFamilies };
        let selectionChanged = false;

        deselectedCategories.forEach((cat) => {
          // Remove product families from selections for this deselected category
          const familiesToRemove = productFamiliesByCategory[cat] || [];
          familiesToRemove.forEach((pf) => {
            if (updatedSelections[pf.product_family_name]) {
              delete updatedSelections[pf.product_family_name];
              selectionChanged = true;
            }
          });
        });

        if (selectionChanged) {
          dispatch(scenarioActions.setGeneratorState({ selectedFamilies: updatedSelections }));
        }

        // Remove product families state for deselected categories
        setProductFamiliesByCategory((prev) => {
          const updated = { ...prev };
          deselectedCategories.forEach((cat) => {
            delete updated[cat];
          });
          return updated;
        });

        // Remove payer mix and groupings for deselected categories
        setPayerMixByCategory((prev) => {
          const updated = { ...prev };
          deselectedCategories.forEach((cat) => { delete updated[cat]; });
          return updated;
        });
        setPayerGroupingsByCategory((prev) => {
          const updated = { ...prev };
          deselectedCategories.forEach((cat) => { delete updated[cat]; });
          return updated;
        });
      }

      // Update the ref for next comparison
      previousCategoriesRef.current = currentCategories;

      // Load product families, payer mix, and payer groupings for newly selected categories
      const userId = formData.logged_in_user;
      formData.categories.forEach((cat) => {
        // Only load if not already loaded
        if (!productFamiliesByCategory[cat]) {
          loadAllProductFamilies(cat);
        }
        if (!payerMixByCategory[cat]) {
          loadPayerMixByCategory(userId, cat);
        }
        if (!payerGroupingsByCategory[cat]) {
          loadPayerGroupings(userId, cat);
        }
      });
    } else if (formData.categories && formData.categories.length === 0) {
      // All categories deselected - clear everything
      previousCategoriesRef.current = [];
      setProductFamiliesByCategory({});
      setPayerMixByCategory({});
      setPayerGroupingsByCategory({});
      dispatch(scenarioActions.setGeneratorState({ selectedFamilies: {} }));
    }
  }, [formData.logged_in_user, formData.categories]);

  // Set default active categories when selection changes
  useEffect(() => {
    if (formData.categories && formData.categories.length > 0) {
      if (!activeCategory || !formData.categories.includes(activeCategory)) {
        setActiveCategory(formData.categories[0]);
      }
    } else {
      setActiveCategory(null);
    }
  }, [formData.categories, activeCategory]);

  // Derive payerMixEligible from the active category's payer mix
  useEffect(() => {
    const mix = activeCategory ? payerMixByCategory[activeCategory] : undefined;
    setPayerMixEligible(!!mix && mix.length > 0);
    setPayerMixLoaded(mix !== undefined);
  }, [activeCategory, payerMixByCategory]);

  const loadUserCategories = async () => {
    if (!formData.logged_in_user) return;
    try {
      const raw = sessionStorage.getItem(CATEGORIES_SESSION_KEY);
      if (raw) {
        const cache: Record<string, string[]> = JSON.parse(raw);
        if (cache[formData.logged_in_user]) {
          setAvailableCategories(cache[formData.logged_in_user]);
          return;
        }
      }
    } catch { /* ignore malformed cache */ }
    try {
      const response = await ApiService.getUserCategories(formData.logged_in_user);
      if (response.data?.categories) {
        setAvailableCategories(response.data.categories);
        try {
          const raw = sessionStorage.getItem(CATEGORIES_SESSION_KEY);
          const cache: Record<string, string[]> = raw ? JSON.parse(raw) : {};
          cache[formData.logged_in_user] = response.data.categories;
          sessionStorage.setItem(CATEGORIES_SESSION_KEY, JSON.stringify(cache));
        } catch { /* storage quota exceeded */ }
      }
    } catch (error) {
      console.error('Failed to load categories:', error);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to load available categories',
        life: 3000,
      });
    }
  };

  const loadAllProductFamilies = async (category: string) => {
    if (!formData.logged_in_user || !category) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Invalid Selection',
        detail: 'Cannot load product families without a valid account and competitive therapeutic area.',
        life: 3000,
      });
      return;
    }
    try {
      const raw = sessionStorage.getItem(PRODUCT_FAMILIES_SESSION_KEY);
      if (raw) {
        const cache: Record<string, Record<string, UserProductFamilies>> = JSON.parse(raw);
        const cached = cache[formData.logged_in_user]?.[category];
        if (cached) {
          setProductFamiliesByCategory((prev) => ({ ...prev, [category]: cached.product_families }));
          dispatch(scenarioActions.setUserProductFamilies(cached));
          return;
        }
      }
    } catch { /* ignore malformed cache */ }
    try {
      const response = await ApiService.getCategoryProductFamiliesNames(
        formData.logged_in_user,
        category
      );
      if (response.data?.product_families) {
        setProductFamiliesByCategory((prev) => ({
          ...prev,
          [category]: response.data.product_families,
        }));
        dispatch(scenarioActions.setUserProductFamilies(response.data));
        try {
          const raw = sessionStorage.getItem(PRODUCT_FAMILIES_SESSION_KEY);
          const cache: Record<string, Record<string, UserProductFamilies>> = raw ? JSON.parse(raw) : {};
          if (!cache[formData.logged_in_user]) cache[formData.logged_in_user] = {};
          cache[formData.logged_in_user][category] = response.data;
          sessionStorage.setItem(PRODUCT_FAMILIES_SESSION_KEY, JSON.stringify(cache));
        } catch { /* storage quota exceeded */ }
      }
    } catch (error) {
      console.error(`Failed to load product families for ${category}:`, error);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: handleApiError(error),
        life: 3000,
      });
    }
  };

  const loadPayerPreferences = async (category: string, mix?: PayerMixItem[]) => {
    if (!formData.logged_in_user) return;
    try {
      let families: ProductFamily[] | null = null;
      try {
        const raw = sessionStorage.getItem(PRODUCT_FAMILIES_SESSION_KEY);
        if (raw) {
          const cache: Record<string, Record<string, UserProductFamilies>> = JSON.parse(raw);
          families = cache[formData.logged_in_user]?.[category]?.product_families ?? null;
        }
      } catch { /* ignore malformed cache */ }

      if (families) {
        await loadDrugPayerMatrixForCategory(category, families, mix);
      } else {
        const response = await ApiService.getCategoryProductFamiliesNames(
          formData.logged_in_user,
          category
        );
        if (response.data?.product_families) {
          await loadDrugPayerMatrixForCategory(category, response.data.product_families, mix);
        }
      }
    } catch (error) {
      console.error(`Failed to load product families for ${category}:`, error);
    }
  }

  // Fetch payer eligibility for a category; default all to true if empty
  const loadDrugPayerMatrixForCategory = async (
    category: string,
    families: ProductFamily[],
    mix?: PayerMixItem[]
  ) => {
    if (!formData.logged_in_user) return;

    // Use provided mix or fall back to the category's stored mix
    const effectiveMix = mix ?? payerMixByCategory[category] ?? [];

    // Set loading state to false initially
    setPayerPreferencesLoaded((prev) => ({ ...prev, [category]: false }));

    try {
      const resp = await ApiService.getDrugPayerMatrix(formData.logged_in_user, category);
      const matrix = resp?.drugPayerMatrix || {};
      const expanded: { [drugId: string]: { [payer: string]: boolean } } = {};
      families.forEach((f) => {
        expanded[f.product_family_id] = matrix[f.product_family_id] || {};
      });

      families.forEach((f) => {
        // Set all payers to true by default, then override with loaded values if present
        const defaultRow: { [payer: string]: boolean } = {};
        effectiveMix.forEach((p) => {
          defaultRow[p.payer] = true;
        });
        expanded[f.product_family_id] = { ...defaultRow, ...(matrix[f.product_family_id] || {}) };
      });

      // Include any extra backend families not currently in list
      Object.keys(matrix).forEach((drugId) => {
        if (!expanded[drugId]) {
          expanded[drugId] = matrix[drugId];
        }
      });

      setDrugPayerMatrix((prev) => ({ ...prev, ...expanded }));

      // Set loading state to true once loaded
      setPayerPreferencesLoaded((prev) => ({ ...prev, [category]: true }));
    } catch (err) {
      console.error('Failed to load drug payer matrix for', category, err);

      // Fallback: initialize defaults if we have families
      const initialized: { [drugId: string]: { [payer: string]: boolean } } = {};
      families.forEach((f) => {
        const defaultRow: { [payer: string]: boolean } = {};
        effectiveMix.forEach((p) => {
          defaultRow[p.payer] = true;
        });
        initialized[f.product_family_id] = defaultRow;
      });

      setDrugPayerMatrix((prev) => ({ ...prev, ...initialized }));

      toast.current?.show({
        severity: 'error',
        summary: 'Eligibility Load Failed',
        detail: 'Using default (all eligible) payer matrix.',
        life: 4500,
      });

      // Set loading state to true even if fallback is used
      setPayerPreferencesLoaded((prev) => ({ ...prev, [category]: true }));
    }
  };

  const loadExcludedOpportunities = async (userId: string) => {
    try {
      const raw = sessionStorage.getItem(EXCLUDED_OPPORTUNITIES_SESSION_KEY);
      if (raw) {
        const cache: Record<string, ExcludedOpportunity[]> = JSON.parse(raw);
        if (cache[userId]) {
          setExcludedOpportunities(cache[userId]);
          return;
        }
      }
    } catch { /* ignore malformed cache */ }
    try {
      const response = await ApiService.getExcludedOpportunities(userId);
      if (response.data) {
        const withIds = response.data.map((item) => ({
          ...item,
          unique_id: `${item.logged_in_user}-${item.category}-${item.contract_opportunities_id}-${item.product_family_id}-${item.tier}`,
        }));

        // Sort by Opportunity then Tier
        withIds.sort((a, b) => {
          const oppA = a.opportunity || '';
          const oppB = b.opportunity || '';
          const cmp = oppA.localeCompare(oppB);
          if (cmp !== 0) return cmp;
          return (a.tier || 0) - (b.tier || 0);
        });

        setExcludedOpportunities(withIds);
        try {
          const raw = sessionStorage.getItem(EXCLUDED_OPPORTUNITIES_SESSION_KEY);
          const cache: Record<string, ExcludedOpportunity[]> = raw ? JSON.parse(raw) : {};
          cache[userId] = withIds;
          sessionStorage.setItem(EXCLUDED_OPPORTUNITIES_SESSION_KEY, JSON.stringify(cache));
        } catch { /* storage quota exceeded */ }
      }
    } catch (error) {
      console.error('Failed to load excluded opportunities:', error);
      setExcludedOpportunities([]);
    }
  }

  // Backfill empty payer preference rows with 100% eligibility once payer mix is known
  useEffect(() => {
    const hasAnyMix = Object.values(payerMixByCategory).some((m) => m.length > 0);
    if (!hasAnyMix) return;
    const loadedCategories = Object.entries(payerPreferencesLoaded)
      .filter(([, isLoaded]) => isLoaded)
      .map(([cat]) => cat);
    if (loadedCategories.length === 0) return;

    setDrugPayerMatrix((prev) => {
      let mutated = false;
      const next: typeof prev = { ...prev };

      loadedCategories.forEach((cat) => {
        const catMix = payerMixByCategory[cat] ?? [];
        const families = productFamiliesByCategory[cat] || [];
        families.forEach((family) => {
          const familyId = family.product_family_id;
          const existingRow = next[familyId] ? { ...next[familyId] } : {};
          let rowChanged = !next[familyId];

          catMix.forEach((payer) => {
            if (!Object.prototype.hasOwnProperty.call(existingRow, payer.payer)) {
              existingRow[payer.payer] = true;
              rowChanged = true;
            }
          });

          if (rowChanged) {
            next[familyId] = existingRow;
            mutated = true;
          }
        });
      });

      return mutated ? next : prev;
    });
  }, [payerMixByCategory, payerPreferencesLoaded, productFamiliesByCategory]);

  // Update matrixReady logic to check loading state for the active category
  const [matrixReady, setMatrixReady] = useState(false);

  useEffect(() => {
    const ready =
      payerMix.length > 0 &&
      Object.keys(drugPayerMatrix).length > 0 &&
      !!activeCategory &&
      !!payerPreferencesLoaded[activeCategory];
    setMatrixReady(ready);
  }, [payerMix, drugPayerMatrix, activeCategory, payerPreferencesLoaded]);

  const validateForm = (data: Partial<ScenarioFormState> = formData): boolean => {
    const errors: Record<string, string> = {};

    if (!data.logged_in_user || !data.logged_in_user.trim()) {
      errors.logged_in_user = 'Account Number is required';
    }

    if (!data.categories || data.categories.length === 0) {
      errors.category = 'At least one competitive therapeutic area is required';
    }

    if (!generateByPayers && (!data.max_scenarios || data.max_scenarios < 1 || data.max_scenarios > 100)) {
      errors.max_scenarios = 'Max scenarios must be between 1 and 100';
    }

    // Validate product families for each selected category
    if (
      !data.categories ||
      data.categories.some(
        (cat) =>
          !productFamiliesByCategory[cat] ||
          productFamiliesByCategory[cat].length === 0
      )
    ) {
      errors.product_families = 'No product families available for the selected account and category';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleGenerateScenarios = async (mode?: 'payer' | 'legacy') => {
    const payerBased = mode ? mode === 'payer' : generateByPayers;
    const effectiveFormData: Partial<ScenarioFormState> = {
      ...formData,
      max_scenarios: payerBased ? (formData.max_scenarios ?? 25) : formData.max_scenarios,
    };

    if (!validateForm(effectiveFormData)) {
      toast.current?.show({
        severity: 'error',
        summary: 'Validation Error',
        detail: 'Please fix the form errors before proceeding',
        life: 3000,
      });
      return;
    }

    // Determine eligible vs ineligible categories based on included product families (>=2 required)
    // A product family only counts as eligible if it is included AND has >0% max market share
    // (i.e. at least one payer is enabled for it)
    const fullMatrixForEligibility = buildFullDrugPayerMatrix();
    const eligibleCategories = (effectiveFormData.categories || []).filter(cat => {
      const pfs = productFamiliesByCategory[cat] || [];
      const catMix = payerMixByCategory[cat] ?? [];
      const effectiveCount = pfs.filter(pf => {
        if (!selectedProductFamilies[pf.product_family_name]?.include) return false;
        // Compute max market share for this PF in this category
        const row = fullMatrixForEligibility[pf.product_family_id] || {};
        const uncheckedPercent = catMix.reduce((sum, item) => {
          const isChecked = row[item.payer] ?? true;
          return !isChecked ? sum + item.percent : sum;
        }, 0);
        const maxShare = Math.max(0, 100 - uncheckedPercent);
        return maxShare > 0;
      }).length;
      return effectiveCount >= 2;
    });
    const ineligibleCategories = (effectiveFormData.categories || []).filter(cat => !eligibleCategories.includes(cat));

    if (ineligibleCategories.length > 0) {
      // Inform user which categories are skipped
      toast.current?.show({
        severity: 'warn',
        summary: 'Skipped Categories',
        detail: `Skipped: ${ineligibleCategories.join(', ')} (needs at least 2 product families).`,
        life: 6000,
      });
    }

    // Payer-optimized generation doesn't support oral inventory. Drop oral categories from a
    // payer run (they stay valid for distribution-based) and tell the user which were skipped.
    let runCategories = eligibleCategories;
    if (payerBased) {
      const oralExcludedCategories = eligibleCategories.filter(cat =>
        (productFamiliesByCategory[cat] ?? []).some(
          pf => (pf.inventory_type ?? '').trim().toLowerCase() === 'oral'
        )
      );
      if (oralExcludedCategories.length > 0) {
        runCategories = eligibleCategories.filter(cat => !oralExcludedCategories.includes(cat));
        toast.current?.show({
          severity: 'warn',
          summary: 'Skipped Categories',
          detail: `Skipped: ${oralExcludedCategories.join(', ')} (oral inventory is not supported by payer-optimized generation).`,
          life: 6000,
        });
      }
    }

    if (runCategories.length === 0) {
      toast.current?.show({
        severity: 'error',
        summary: 'No Eligible Categories',
        detail: 'Select categories that have at least 2 included product families.',
        life: 4000,
      });
      return;
    }

    setIsGenerating(true);
    setGenerationProgress(0);

    try {
      // Simulate progress updates
      const progressInterval = setInterval(() => {
        setGenerationProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return prev;
          }
          return prev + 10;
        });
      }, 500);

      // Generate and calculate scenarios
      // Build min_distribution_map from selectedProductFamilies
      // Aggregate product families across all selected categories
      const aggregatedFamilies: ProductFamily[] = Object.values(productFamiliesByCategory).flat();
      const nameToFamily = new Map<string, ProductFamily>();
      aggregatedFamilies.forEach(pf => nameToFamily.set(pf.product_family_name, pf));

      const minDistMap: { [key: string]: number } = {};
      const feesMap: { [key: string]: number } = {};
      const directRebatesMap: { [key: string]: number } = {};
      Object.entries(selectedProductFamilies).forEach(([pfName, selection]) => {
        if (!selection.include) return; // skip excluded
        const pf = nameToFamily.get(pfName);
        if (pf) {
          // Store clamped minDistribution (0-1) using numeric id key; guard against float drift
          const raw = selection.minDistribution ?? 0;
          const clamped = Math.max(0, Math.min(1, Number(raw) || 0));
          minDistMap[pf.product_family_id.toString()] = clamped;
          if (typeof selection.adminFeePerEu === 'number') {
            feesMap[pf.product_family_id.toString()] = Math.round(selection.adminFeePerEu * 100) / 100;
          }
          // Clamped at 0 here too, not just in the input: a stale negative could survive in
          // cached generator state from before the input existed.
          if (typeof selection.directRebatePerEu === 'number') {
            directRebatesMap[pf.product_family_id.toString()] =
              Math.max(0, Math.round(selection.directRebatePerEu * 100) / 100);
          }
        }
      });
      // If no selections but we have families, default all included at 0 to satisfy API expectation
      if (Object.keys(minDistMap).length === 0 && aggregatedFamilies.length > 0) {
        aggregatedFamilies.forEach(pf => {
          minDistMap[pf.product_family_id.toString()] = 0;
        });
      }
      // Ensure we send a fully expanded matrix with defaults (true) for missing payer entries
      const fullMatrixForGeneration = buildFullDrugPayerMatrix();

      // Calculate max shares dynamically from the full matrix to ensure consistency
      // This fixes issues where stale or missing drugMaxShares caused zero-out in min_distribution_map
      const computedMaxSharesMap: Record<string, number> = {};
      Object.entries(productFamiliesByCategory).forEach(([cat, families]) => {
        const catMix = payerMixByCategory[cat] ?? [];
        families.forEach((pf) => {
          const row = fullMatrixForGeneration[pf.product_family_id] || {};
          const uncheckedPercent = catMix.reduce((sum, item) => {
            const isChecked = row[item.payer] ?? true;
            return !isChecked ? sum + item.percent : sum;
          }, 0);
          computedMaxSharesMap[pf.product_family_id] = Math.round(Math.max(0, 100 - uncheckedPercent) * 10) / 10;
        });
      });

      dispatch(scenarioActions.setGeneratorState({
        form: {
          ...effectiveFormData,
          drugPayerMatrix: fullMatrixForGeneration,
          // Cache admin fees (keyed by product_family_id) so they carry to the
          // results page and can be re-applied when recalculating alternates.
          product_family_fees_map: feesMap,
          product_family_direct_rebates_map: directRebatesMap,
          // Cache per-category volume scaling so alternate recalculations on the
          // results page model the same category size as the generated scenarios.
          category_scale_factor_map: categoryScaleFactors,
        } as any
      }));

      if (userName) {
        dispatch(scenarioActions.setCurrentOrg(userName))
      }
      if (effectiveFormData.logged_in_user) dispatch(scenarioActions.setCurrentUser(effectiveFormData.logged_in_user));
      if (effectiveFormData.categories && effectiveFormData.categories.length > 0) {
        dispatch(scenarioActions.setCurrentCategory(effectiveFormData.categories[0]));
      }

      // Initialize generation session only for eligible categories
      dispatch(scenarioActions.initGenerationSession(runCategories.length));

      const categories = [...runCategories];
      if (categories.length === 0) throw new Error('No eligible categories after filtering');

      const firstCategory = categories.shift()!; // first eligible category

      // Build excludedScenarios from selectedExcludedOpportunities for this category
      const buildExcludedScenarios = (category: string) => {
        return selectedExcludedOpportunities
          .filter(opp => opp.category === category && !isProductFamilyNotIncluded(opp))
          .map(opp => ({
            logged_in_user: effectiveFormData.logged_in_user || '',
            category: opp.category || category,
            product_family_id: Number(opp.product_family_id),
            paid_on: opp.paid_on || '',
            rebate: Number(opp.rebate) ?? 0,
          }));
      };

      const buildPayload = (category: string): ScenarioRequestWithMinsAndMaxAndPvt => {
        const catFamilyIds = new Set(
          (productFamiliesByCategory[category] ?? [])
            .filter(pf => selectedProductFamilies[pf.product_family_name]?.include)
            .map(pf => pf.product_family_id.toString())
        );
        const catMinDistMap = Object.fromEntries(
          Object.entries(minDistMap).filter(([drugId]) =>
            catFamilyIds.has(drugId) && (computedMaxSharesMap[drugId] ?? 0) > 0
          )
        );
        const catMaxSharesMap = Object.fromEntries(
          Object.entries(computedMaxSharesMap).filter(([drugId]) => catFamilyIds.has(drugId.toString()))
        );
        const catMatrix = Object.fromEntries(
          Object.entries(formData.drugPayerMatrix ?? fullMatrixForGeneration)
            .filter(([drugId]) => catFamilyIds.has(drugId.toString()))
        );
        const catFeesMap = Object.fromEntries(
          Object.entries(feesMap).filter(([drugId]) => catFamilyIds.has(drugId))
        );
        const catDirectRebatesMap = Object.fromEntries(
          Object.entries(directRebatesMap).filter(([drugId]) => catFamilyIds.has(drugId))
        );
        return {
          logged_in_user: effectiveFormData.logged_in_user || '',
          category,
          max_scenarios: effectiveFormData.max_scenarios as any,
          min_distribution_map: catMinDistMap,
          product_family_fees_map: Object.keys(catFeesMap).length > 0 ? catFeesMap : undefined,
          product_family_direct_rebates_map: Object.keys(catDirectRebatesMap).length > 0 ? catDirectRebatesMap : undefined,
          drugPayerMatrix: catMatrix,
          drugMaxShares: catMaxSharesMap,
          excludedScenarios: buildExcludedScenarios(category),
          category_scale_factor: categoryScaleFactors[category] ?? 1.0,
          batch_or_alternate: 'batch',
        };
      };

      localStorage.removeItem('scenarioiq_favorites_ui_cache'); // Clear favorites cache on new generation

      const generateAndCalculate = payerBased
        ? ApiService.generateAndCalculateScenariosWithMinsAndMaxAndPvtGreedy
        : ApiService.generateAndCalculateScenariosWithMinsAndMaxAndPvt;

      // Generate first category fast
      const firstResponse = await generateAndCalculate(buildPayload(firstCategory));
      if (firstResponse.success) {
        const firstSummaries = (Array.isArray(firstResponse.summary) ? firstResponse.summary : [firstResponse.summary]).map(s => ({ ...s, category: firstCategory }));
        dispatch(scenarioActions.setScenarioResults(firstResponse.results, firstSummaries));
        if (firstResponse.profitability) {
          dispatch(scenarioActions.mergePayerProfitability(
            Object.fromEntries(Object.entries(firstResponse.profitability).map(([sid, v]) => [Number(sid), v]))
          ));
        }
        if (firstResponse.rebate_details && firstResponse.rebate_details.length > 0) {
          dispatch(scenarioActions.mergeRebateDetails(firstResponse.rebate_details));
        }
        if (firstResponse.excluded_payers && firstResponse.excluded_payers.length > 0 && firstResponse.excluded_payer_volume != null) {
          dispatch(scenarioActions.setExcludedPayerInfo(firstCategory, firstResponse.excluded_payers, firstResponse.excluded_payer_volume));
        }
        if (firstResponse.missing_fee_schedule_pf_ids && firstResponse.missing_fee_schedule_pf_ids.length > 0) {
          dispatch(scenarioActions.setMissingFeeSchedulePfIds(firstCategory, firstResponse.missing_fee_schedule_pf_ids));
        }
        if (firstResponse.fee_schedule_gaps && Object.keys(firstResponse.fee_schedule_gaps).length > 0) {
          dispatch(scenarioActions.setFeeScheduleGaps(firstCategory, firstResponse.fee_schedule_gaps));
        }
        dispatch(scenarioActions.incrementGenerationCompleted());
        clearInterval(progressInterval);
        setGenerationProgress(35); // show some immediate progress

        toast.current?.show({
          severity: 'info',
          summary: 'Partial Results Ready',
          detail: `First competitive therapeutic area (${firstCategory}) generated. Continuing with remaining CTAs...`,
          life: 4000,
        });

        // Navigate immediately so user can see first batch
        navigate('/results');
      } else {
        throw new Error('Failed generating first competitive therapeutic area');
      }

      // Process remaining categories sequentially (background)
      for (const category of categories) {
        try {
          const resp = await generateAndCalculate(buildPayload(category));
          if (resp.success) {
            const categorySummaries = (Array.isArray(resp.summary) ? resp.summary : [resp.summary]).map(s => ({ ...s, category }));
            dispatch(
              scenarioActions.appendScenarioResults(
                resp.results,
                categorySummaries
              )
            );
            if (resp.profitability) {
              dispatch(scenarioActions.mergePayerProfitability(
                Object.fromEntries(Object.entries(resp.profitability).map(([sid, v]) => [Number(sid), v]))
              ));
            }
            if (resp.rebate_details && resp.rebate_details.length > 0) {
              dispatch(scenarioActions.mergeRebateDetails(resp.rebate_details));
            }
            if (resp.excluded_payers && resp.excluded_payers.length > 0 && resp.excluded_payer_volume != null) {
              dispatch(scenarioActions.setExcludedPayerInfo(category, resp.excluded_payers, resp.excluded_payer_volume));
            }
            if (resp.missing_fee_schedule_pf_ids && resp.missing_fee_schedule_pf_ids.length > 0) {
              dispatch(scenarioActions.setMissingFeeSchedulePfIds(category, resp.missing_fee_schedule_pf_ids));
            }
            if (resp.fee_schedule_gaps && Object.keys(resp.fee_schedule_gaps).length > 0) {
              dispatch(scenarioActions.setFeeScheduleGaps(category, resp.fee_schedule_gaps));
            }
            dispatch(scenarioActions.incrementGenerationCompleted());
            setGenerationProgress((prev) => Math.min(prev + Math.round(50 / categories.length), 95));
            toast.current?.show({
              severity: 'success',
              summary: 'Competitive Therapeutic Area Complete',
              detail: `Finished generating ${category}`,
              life: 2500,
            });
          }
        } catch (err) {
          console.error('Background competitive therapeutic area generation failed', err);
          toast.current?.show({
            severity: 'warn',
            summary: 'Partial Failure',
            detail: `Failed generating ${category}. Continuing...`,
            life: 3000,
          });
        }
      }

      clearInterval(progressInterval);
      setGenerationProgress(100);
      dispatch(scenarioActions.endGenerationSession());
      toast.current?.show({
        severity: 'success',
        summary: 'All Categories Complete',
        detail: 'Finished generating scenarios for all categories.',
        life: 3000,
      });

    } catch (error) {
      console.error('Scenario generation failed:', error);
      toast.current?.show({
        severity: 'error',
        summary: 'Generation Failed',
        detail: handleApiError(error),
        life: 5000,
      });
    } finally {
      setIsGenerating(false);
      setTimeout(() => setGenerationProgress(0), 2000);
    }
  };

  const handleCategoryScaleChange = (category: string, nextFactor: number) => {
    setCategoryScaleFactors(prev => {
      const next = { ...prev, [category]: nextFactor };
      // Persist immediately so the value survives navigation to the results page.
      dispatch(scenarioActions.setGeneratorState({
        form: { ...formData, category_scale_factor_map: next },
      }));
      return next;
    });
  };

  const handleInputChange = (field: keyof ScenarioFormState, value: any) => {
    let updatedValue = value;
    // Safeguard: ensure categories is always an array
    if (field === 'categories') {
      updatedValue = Array.isArray(value) ? value : [];
    }

    dispatch(scenarioActions.setGeneratorState({
      form: { ...formData, [field]: updatedValue }
    }));

    // Clear validation error for this field
    if (validationErrors[field]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  useEffect(() => {
    if (!generateByPayers) return;
    // When payer-based generation is enabled, keep max scenarios valid even though the input is disabled.
    if (formData.max_scenarios === null || formData.max_scenarios === undefined) {
      handleInputChange('max_scenarios', 25);
    }
  }, [generateByPayers, formData.max_scenarios]);

  useEffect(() => {
    // Clear potentially stale max shares from previous sessions on mount
    if (Object.keys(formData.drugMaxShares || {}).length > 0) {
      dispatch(scenarioActions.setGeneratorState({
        form: { ...formData, drugMaxShares: {} }
      }));
    }

    // On mount, if we have a cached user but no categories, reload them.
    if (formData.logged_in_user && availableCategories.length === 0) {
      loadUserCategories();
    }
  }, []);


  const categoryOptions = useMemo(() => availableCategories.map(cat => ({
    label: cat,
    value: cat,
  })), [availableCategories]);

  const distributionBodyTemplate = (rowData: ProductFamily) => {
    return (
      <div className="flex align-items-center gap-2">
        {/* <ProgressBar
          value={rowData.current_distribution * 100}
          style={{ width: '100px', height: '8px' }}
          showValue={false}
        /> */}
        <span className="text-sm">
          {(rowData.current_distribution * 100).toFixed(1)}%
        </span>
      </div>
    );
  };

  const distributionSpendBodyTemplate = (rowData: ProductFamily) => {
    return (
      <div className="flex align-items-center gap-2">
        {/* <ProgressBar
          value={rowData.current_spend_distribution * 100}
          style={{ width: '100px', height: '8px' }}
          showValue={false}
        /> */}
        <span className="text-sm">
          {(rowData.current_spend_distribution * 100).toFixed(1)}%
        </span>
      </div>
    );
  };

  const statusBodyTemplate = (rowData: ProductFamily) => {
    return (
      <Tag
        value={rowData.current_distribution > 0 ? 'Active' : 'Inactive'}
        severity={rowData.current_distribution > 0 ? 'success' : 'warning'}
        rounded
      />
    );
  };

  // Product families to show in Drug ↔ Payer Eligibility dialog (only active category)
  const eligibilityFamilies = useMemo(() => {
    const families = activeCategory ? (productFamiliesByCategory[activeCategory] || []) : [];
    return [...families].sort((a, b) => {
      const nameA = String(a.product_family_name ?? '').trim();
      const nameB = String(b.product_family_name ?? '').trim();
      return nameA.localeCompare(nameB, undefined, { sensitivity: 'base' });
    });
  }, [activeCategory, productFamiliesByCategory]);

  // Toggle payers for a single product family row
  const toggleRowAll = (pfId: number | string, nextValue: boolean) => {
    const id = String(pfId);
    setDrugPayerMatrix((prev) => {
      const row = { ...(prev[id] || {}) };
      visiblePayerMix.forEach((item) => {
        row[item.payer] = nextValue;
      });
      return { ...prev, [id]: row };
    });
    setMatrixDirty(true);
  };

  // Check if every cell in the matrix is selected (for all eligible families × all payers)
  const isEntireMatrixSelected = useMemo(() => {
    if (!matrixReady || eligibilityFamilies.length === 0 || visiblePayerMix.length === 0) return false;
    return eligibilityFamilies.every((pf) => {
      const isSelected = selectedProductFamilies[pf.product_family_name]?.include ?? true;
      if (!isSelected) return true; // skip disabled rows
      return visiblePayerMix.every((item) => drugPayerMatrix[pf.product_family_id]?.[item.payer] ?? true);
    });
  }, [matrixReady, eligibilityFamilies, visiblePayerMix, drugPayerMatrix, selectedProductFamilies]);

  // Toggle all payers for all product families in the matrix
  const toggleAllMatrix = (nextValue: boolean) => {
    setDrugPayerMatrix((prev) => {
      const next = { ...prev };
      eligibilityFamilies.forEach((pf) => {
        const isSelected = selectedProductFamilies[pf.product_family_name]?.include ?? true;
        if (!isSelected) return;
        const row = { ...(next[pf.product_family_id] || {}) };
        visiblePayerMix.forEach((item) => {
          row[item.payer] = nextValue;
        });
        next[pf.product_family_id] = row;
      });
      return next;
    });
    setMatrixDirty(true);
  };

  // Build a fully expanded matrix across all loaded product families & payers
  const buildFullDrugPayerMatrix = (): { [drugId: string]: { [payer: string]: boolean } } => {
    const full: { [drugId: string]: { [payer: string]: boolean } } = {};
    Object.entries(productFamiliesByCategory).forEach(([cat, families]) => {
      const catMix = payerMixByCategory[cat] ?? [];
      families.forEach(f => {
        const existingRow = drugPayerMatrix[f.product_family_id] || {};
        const row: { [payer: string]: boolean } = {};
        catMix.forEach(p => {
          row[p.payer] = Object.prototype.hasOwnProperty.call(existingRow, p.payer) ? existingRow[p.payer] : true;
        });
        full[f.product_family_id] = row;
      });
    });
    return full;
  };

  // Helper: compute drugMaxShares from a given matrix for provided families using a given payer mix
  const computeDrugMaxShares = (
    matrix: { [drugId: string]: { [payer: string]: boolean } },
    families: ProductFamily[],
    mix: PayerMixItem[] = payerMix
  ): Record<string, number> => {
    return families
      .map((pf) => {
        const uncheckedPercent = mix.reduce((acc, item) => {
          const isChecked = matrix[pf.product_family_id]?.[item.payer] ?? true;
          return !isChecked ? acc + item.percent : acc;
        }, 0);
        // Clamp to [0, 100] and round to one decimal
        const maxMarketShare = Math.max(0, Math.min(100, 100 - uncheckedPercent));
        return { productFamilyId: pf.product_family_id, maxMarketShare: Math.round(maxMarketShare * 10) / 10 };
      })
      .reduce<Record<string, number>>((acc, d) => {
        acc[d.productFamilyId] = d.maxMarketShare;
        return acc;
      }, {});
  };

  // Keep a fully expanded matrix in formData for generation even if user cancels dialog
  useEffect(() => {
    // Need at least one category with a loaded payer mix
    const hasAnyMix = Object.values(payerMixByCategory).some((m) => m.length > 0);
    if (!hasAnyMix) return;
    const full = buildFullDrugPayerMatrix();
    // Compute max shares per category then merge
    const nextMaxShares: Record<string, number> = {};
    Object.entries(productFamiliesByCategory).forEach(([cat, families]) => {
      const catMix = payerMixByCategory[cat] ?? [];
      if (families.length > 0 && catMix.length > 0) {
        const catShares = computeDrugMaxShares(full, families, catMix);
        Object.assign(nextMaxShares, catShares);
      }
    });

    dispatch(
      scenarioActions.setGeneratorState({
        form: {
          ...formData,
          drugPayerMatrix: full,
          drugMaxShares: nextMaxShares,
        },
      })
    );
  }, [drugPayerMatrix, payerMixByCategory, productFamiliesByCategory]);

  const isProductFamilyNotIncluded = (data: ExcludedOpportunity): boolean => {
    if (data.product_family_id !== undefined && data.product_family_id !== null) {
      const family = productFamiliesById[String(data.product_family_id)];
      if (family) {
        return !(selectedProductFamilies[family.product_family_name]?.include ?? false);
      }
    }

    if (!data.product_family_name) return false;
    const targetName = data.product_family_name.trim();
    const selectedKey = Object.keys(selectedProductFamilies).find(
      (name) => name.trim() === targetName
    );
    return !(selectedKey && selectedProductFamilies[selectedKey]?.include);
  };

  const pruneDisabledExcludedOpportunities = (
    nextSelectedFamilies: Record<string, ProductFamilySelection> = selectedProductFamilies
  ) => {
    if (selectedExcludedOpportunities.length === 0) return;

    const filteredSelectedOpportunities = selectedExcludedOpportunities.filter((opp) => {
      if (opp.product_family_id !== undefined && opp.product_family_id !== null) {
        const family = productFamiliesById[String(opp.product_family_id)];
        if (family) {
          return nextSelectedFamilies[family.product_family_name]?.include ?? false;
        }
      }

      if (!opp.product_family_name) return true;
      const targetName = opp.product_family_name.trim();
      const selectedKey = Object.keys(nextSelectedFamilies).find(
        (name) => name.trim() === targetName
      );
      return selectedKey ? (nextSelectedFamilies[selectedKey]?.include ?? false) : false;
    });

    if (filteredSelectedOpportunities.length === selectedExcludedOpportunities.length) return;

    setSelectedExcludedOpportunities(filteredSelectedOpportunities);
    dispatch(scenarioActions.setGeneratorState({
      form: {
        ...formData,
        selectedExcludedOpportunities: filteredSelectedOpportunities,
      },
    }));
  };

  useEffect(() => {
    pruneDisabledExcludedOpportunities(selectedProductFamilies);
  }, [dispatch, formData, selectedExcludedOpportunities, selectedProductFamilies]);

  const isExcludedOpportunitySelectable = (rowData: ExcludedOpportunity): boolean => {
    // If product family is not selected in Advanced Settings, disable it
    if (isProductFamilyNotIncluded(rowData)) {
      return false;
    }
    // If this row is already selected, it's always selectable (to allow deselection)
    if (selectedExcludedOpportunities.some(opp => opp.unique_id === rowData.unique_id)) {
      return true;
    }
    // Check if any other tier from the same contract opportunity is already selected
    const hasOtherTierSelected = selectedExcludedOpportunities.some(
      opp => opp.contract_opportunities_id === rowData.contract_opportunities_id && opp.unique_id !== rowData.unique_id
    );
    return !hasOtherTierSelected;
  };

  const getDisabledOpportunityTooltip = (rowData: ExcludedOpportunity): string => {
    if (isProductFamilyNotIncluded(rowData)) {
      return `The product family "${rowData.product_family_name}" hasn't been selected for generation`;
    }
    return "Another tier for this opportunity is already selected";
  };

  const wrapWithOpportunityTooltip = (content: React.ReactNode, rowData: ExcludedOpportunity) => {
    const disabled = !isExcludedOpportunitySelectable(rowData);
    const exclusionReason = rowData.exclusion_reason?.trim();
    const tooltip = disabled ? getDisabledOpportunityTooltip(rowData) : exclusionReason || undefined;
    return (
      <span
        className="excl-opp-cell"
        data-pr-tooltip={tooltip}
        style={{ display: 'block', pointerEvents: disabled ? 'auto' : undefined }}
        onClick={disabled ? (e) => e.stopPropagation() : undefined}
      >
        {content}
      </span>
    );
  };

  const applyOpportunities = (selectedOpportunities: ExcludedOpportunity[]) => {
    const enabledOpportunities = selectedOpportunities.filter((opp) => !isProductFamilyNotIncluded(opp));
    setSelectedExcludedOpportunities(enabledOpportunities);
    setShowExcludedOpportunities(false);
    dispatch(scenarioActions.setGeneratorState({
      form: {
        ...formData,
        selectedExcludedOpportunities: enabledOpportunities,
      },
    }));
    toast.current?.show({
      severity: 'success',
      summary: 'Saved',
      detail: `${enabledOpportunities.length} excluded opportunities applied.`,
      life: 2500,
    });
  }

  // Matrix readiness: only allow opening/editing once payer mix AND at least one matrix row loaded
  const matrixReadyForUI = payerMix.length > 0 && Object.keys(drugPayerMatrix).length > 0;

  // Workaround for a PrimeReact InputNumber bug (mode="currency"): backspacing the
  // currency symbol ("$") corrupts the value (e.g. $100 -> 10100) because its internal
  // handler treats the missing minus sign (index -1) as adjacent to the currency symbol
  // (index 0). Block Backspace only when the char before the caret is a non-numeric
  // symbol like "$"; digits, separators, decimal point and the minus sign are untouched.
  const blockCurrencySymbolBackspace = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Backspace') return;
    const input = e.target as HTMLInputElement;
    const { selectionStart, selectionEnd, value } = input;
    if (selectionStart === null || selectionStart !== selectionEnd) return; // allow range deletes
    const charBefore = value.charAt(selectionStart - 1);
    if (charBefore && /[^0-9.,\-\s]/.test(charBefore)) {
      e.preventDefault();
    }
  };

  return (
    <div className="scenario-generator-container">
      <Toast ref={toast} />

      <div className="grid">
        {/* Configuration Form */}
        <div className="col-12 lg:col-6">
          <Card title="Scenario Configuration" className="h-full">
            <div className="p-fluid">
              {/* Org Name (Editable now) */}
              <div className="field" style={{ marginBottom: '3rem' }}>
                  <div className="flex justify-content-end align-items-center mb-1">
                    <Button
                      icon="pi pi-times"
                      tooltip="Clear Selections"
                      tooltipOptions={{ position: 'left' }}
                      rounded
                      text
                      size="small"
                      style={{ border: 'none', width: '1.75rem', height: '1.75rem', padding: 0, color: '#173A63' }}
                      onClick={() => {
                        setMatrixReady(false);
                        setPayerPreferencesLoaded({});
                        dispatch(scenarioActions.setGeneratorState({
                          ...(orgDropdownLocked ? {} : { userName: '' }),
                          form: {
                            logged_in_user: orgDropdownLocked ? formData.logged_in_user : '',
                            categories: [],
                            max_scenarios: 25,
                            selectedExcludedOpportunities: [],
                            drugPayerMatrix: {},
                            drugMaxShares: {},
                          },
                          selectedFamilies: {},
                        }));
                        setProductFamilies([]);
                        setProductFamiliesByCategory({});
                        if (!orgDropdownLocked) {
                          setAvailableCategories([]);
                          setExcludedOpportunities([]);
                          setUserIdInput('');
                        }
                        setSelectedExcludedOpportunities([]);
                        setDrugPayerMatrix({});
                        setValidationErrors({});
                        setPayerMixByCategory({});
                        setPayerGroupingsByCategory({});
                        setPayerMixEligible(false);
                        setPayerMixLoaded(false);
                      }}
                      disabled={!formData.logged_in_user}
                    />
                  </div>
                  <div className="sup-label-field">
                    <label htmlFor="orgName" className="font-medium">
                      Organization Name <span className="text-red-500">*</span>
                    </label>
                  <AutoComplete
                    id="orgName"
                    value={userName || ''}
                    suggestions={filteredCustomers}
                    completeMethod={searchCustomer}
                    minLength={0}
                    onDropdownClick={() => setFilteredCustomers([...allCustomers])}
                    itemTemplate={(item: string) => {
                      // Extract display name from unique key (name|||accountNumber)
                      const displayName = getDisplayNameFromKey(item);
                      const isCorporate = corporateParentSet.has(item);
                      const isChildPractice = displayName.startsWith('    '); // Child practices are indented
                      const isOrphanChild = orphanChildKeysRef.current.has(item); // Child whose parent isn't in results
                      const isStandalonePractice = !isCorporate && !isChildPractice; // Practice without a parent
                      return (
                        <div className="flex align-items-center gap-2">
                          {isCorporate ? (
                            <i className="pi pi-building" style={{ fontSize: '1.1rem', color: '#173A63' }} />
                          ) : (isChildPractice && !isOrphanChild) ? (
                            <i className="pi pi-home" style={{ fontSize: '0.9rem', color: '#6c757d', marginLeft: '1rem' }} />
                          ) : isOrphanChild ? (
                            <i className="pi pi-home" style={{ fontSize: '0.9rem', color: '#495057' }} />
                          ) : isStandalonePractice ? (
                            <i className="pi pi-briefcase" style={{ fontSize: '0.9rem', color: '#495057' }} />
                          ) : (
                            <i className="pi pi-home" style={{ fontSize: '0.9rem', color: '#6c757d' }} />
                          )}
                          <span>{displayName.trim()}</span>
                        </div>
                      );
                    }}
                    onChange={(e) => {
                      // Deliberately NOT trimmed: a space is the trailing character at the moment
                      // it is typed, so trimming here stripped it every keystroke and made org
                      // names with spaces impossible to type. Matching trims instead (searchCustomer),
                      // and onSelect replaces this with the canonical display name anyway.
                      const rawValue = e.value;
                      // Extract display name if it contains our delimiter (user selected from dropdown)
                      const newUserName = getDisplayNameFromKey(rawValue);
                      dispatch(scenarioActions.setGeneratorState({ userName: newUserName }));
                      if (!newUserName || newUserName.trim() === '') {
                        // Clear all user-dependent state
                        dispatch(scenarioActions.setGeneratorState({
                          form: {
                            ...formData,
                            logged_in_user: '', // Clear the user ID
                            categories: [],
                            selectedExcludedOpportunities: [],
                          },
                          selectedFamilies: {},
                        }));
                        setAvailableCategories([]);
                        setSelectedExcludedOpportunities([]);
                        setExcludedOpportunities([]);
                        setProductFamiliesByCategory({});
                        setUserIdInput('');
                        setPayerMixByCategory({});
                        setPayerGroupingsByCategory({});
                        setPayerMixEligible(false);
                        setPayerMixLoaded(false);
                      } else if (!rawValue.includes(PRACTICE_KEY_DELIMITER)) {
                        // User is typing a partial search string (not a confirmed selection).
                        // Disable categories immediately so stale data from the previous org
                        // is not accessible until onSelect confirms the new org.
                        dispatch(scenarioActions.setGeneratorState({
                          form: {
                            ...formData,
                            logged_in_user: '',
                            categories: [],
                          },
                          selectedFamilies: {},
                        }));
                        setAvailableCategories([]);
                        setProductFamiliesByCategory({});
                        setPayerMixByCategory({});
                        setPayerGroupingsByCategory({});
                        setPayerMixEligible(false);
                        setPayerMixLoaded(false);
                      }
                    }}
                    onSelect={async (e) => {
                      const selected = e.value; // This is the full unique key (name|||accountNumber)
                      // Extract just the display name for showing in the input field
                      const displayName = getDisplayNameFromKey(typeof selected === 'string' ? selected.trim() : selected);
                      dispatch(scenarioActions.setGeneratorState({ userName: displayName }));

                      if (allCustomers.includes(selected)) {
                        try {

                          // Get account number from our practice map using the full unique key
                          const accountNumber = practiceMap[selected];
                          if (accountNumber) {
                            const newUserId = accountNumber;
                            loadExcludedOpportunities(newUserId);
                            if (newUserId !== formData.logged_in_user) {
                              // Clear out old data when switching users
                              setMatrixReady(false);
                              setPayerPreferencesLoaded({});
                              dispatch(scenarioActions.setGeneratorState({
                                form: {
                                  logged_in_user: newUserId,
                                  categories: [],
                                  max_scenarios: 25,
                                },
                                selectedFamilies: {},
                              }));
                              setProductFamilies([]);
                              setProductFamiliesByCategory({});
                              setDrugPayerMatrix({});
                              setPayerMixByCategory({});
                              setPayerGroupingsByCategory({});
                              setPayerMixEligible(false);
                              setPayerMixLoaded(false);
                              setAvailableCategories([]); // Clear categories so they get refetched
                              setUserIdInput(newUserId);
                              // Categories will be refetched via the useEffect that watches formData.logged_in_user
                            }
                          }
                        }
                        catch (err) {
                          console.error("Failed to fetch user ID:", err);
                        }
                      }
                    }}
                    placeholder="Enter organization name"
                    spellCheck={false}
                    dropdown
                    // forceSelection
                    disabled={orgDropdownLocked}
                    className={validationErrors.logged_in_user ? 'p-invalid' : ''}
                    scrollHeight="400px"
                    appendTo="self"
                  />
                {validationErrors.logged_in_user && (
                  <small className="p-error">{validationErrors.logged_in_user}</small>
                )}
                  </div>
              </div>

              <div className="field" hidden>
                <label htmlFor="userId" className="font-medium">
                  Account Number
                </label>
                <InputText
                  id="userId"
                  readOnly
                  value={userIdInput}
                  onChange={(e) => setUserIdInput(e.target.value)}
                  onBlur={() => {
                    if (userIdInput !== formData.logged_in_user) {
                      handleInputChange('logged_in_user', userIdInput);
                      handleInputChange('categories', []);
                      setProductFamilies([]);                  // reset product families
                    }
                  }}
                  placeholder="Account number"
                  className={validationErrors.logged_in_user ? 'p-invalid' : ''}
                  disabled={!formData.logged_in_user || availableCategories.length === 0}
                />
                {validationErrors.logged_in_user && (
                  <small className="p-error">{validationErrors.logged_in_user}</small>
                )}
              </div>

                {/* Category */}
                <div className="field">
                  <div className="sup-label-field">
                    <label htmlFor="category" className="font-medium">
                      Competitive Therapeutic Area <span className="text-red-500">*</span>
                    </label>
                    <MultiSelect
                      id="category"
                      value={formData.categories || []}
                      options={categoryOptions}
                      onChange={(e) => {
                        // Ensure value is always an array to prevent crashes
                        const value = Array.isArray(e.value) ? e.value : [];
                        handleInputChange('categories', value);
                      }}
                      placeholder="Search & select CTAs"
                      className={validationErrors.category ? 'p-invalid' : ''}
                      disabled={!formData.logged_in_user || !userName || availableCategories.length === 0}
                      filter
                      filterBy="label"
                      panelHeaderTemplate={(options) => (
                        <div className="p-multiselect-header" style={{ padding: '0.5rem' }}>
                          {options.filterElement}
                        </div>
                      )}
                    />
                  </div>
                  {validationErrors.category && (
                  <small className="p-error">{validationErrors.category}</small>
                  )}
                </div>

              {/* Algorithm / Strategy Selection */}
              {formData.categories && formData.categories.length > 0 && (
                <div className="field">
                  <label className="font-medium mb-2 block">
                    Generation Strategy{' '}
                    <i
                      className="pi pi-info-circle"
                      data-pr-tooltip={
                        'Payer-Optimized:\n' +
                        'Best drug per payer using reimbursement, rebates, and pricing\n\n' +
                        'Distribution-Based:\n' +
                        'Scenarios using overall drug mix, no payer-level optimization'
                      }
                      data-pr-position="right"
                      style={{
                        cursor: 'pointer',
                        marginLeft: '0.5rem',
                        color: '#6c757d',
                        fontSize: '1rem'
                      }}
                    />
                  </label>
                  <div className="flex align-items-center gap-1">
                    <Button
                      label="Payer-Optimized"
                      icon="pi pi-sitemap"
                      className={`p-button-sm ${generateByPayers ? 'p-button-primary' : 'p-button-outlined'}`}
                      onClick={() => {
                        setGenerateByPayers(true);
                        dispatch(scenarioActions.setGeneratorState({ preferredGenerationMode: 'payer' }));
                      }}
                      disabled={!payerMixEligible || hasOralInventory}
                    />
                    <Button
                      label="Distribution-Based"
                      icon="pi pi-sliders-h"
                      className={`p-button-sm ${!generateByPayers ? 'p-button-primary' : 'p-button-outlined'}`}
                      onClick={() => {
                        setGenerateByPayers(false);
                        dispatch(scenarioActions.setGeneratorState({ preferredGenerationMode: 'distribution' }));
                      }}
                    />
                  </div>
                </div>
              )}

              <Divider />

              {/* Advanced Settings */}
              <Accordion>
                <AccordionTab header="Advanced Settings">
                  {formData.categories && formData.categories.length > 0 ? (
                    <div>
                      <div className="scroll-tabs mb-3">
                        {formData.categories.map(cat => (
                          <button
                            key={cat}
                            type="button"
                            className={`scroll-tab ${activeCategory === cat ? 'active' : ''}`}
                            onClick={() => setActiveCategory(cat)}
                          >
                            {cat}
                          </button>
                        ))}
                      </div>
                      <label className="font-medium mb-2 block">Product Families and Minimum Distribution</label>
                      {(activeCategory && productFamiliesByCategory[activeCategory]?.length) ? (
                        <div style={{ display: 'grid', width: '100%', gridTemplateColumns: 'minmax(0, 1fr) 90px 125px 100px', columnGap: '0.5rem', rowGap: '0.5rem', alignItems: 'center' }}>
                          <div
                            className="sticky-select-all"
                            style={{ gridColumn: '1 / -1', display: 'grid', gridTemplateColumns: 'subgrid', alignItems: 'center', columnGap: '0.5rem', marginBottom: 0 }}
                          >
                            <div className="flex align-items-center">
                              <Checkbox
                                inputId="pf-select-all"
                                checked={isAllFamiliesSelectedInActiveCategory}
                                inputRef={selectAllCheckboxInputRef}
                                onChange={toggleSelectAllFamiliesInActiveCategory}
                              />
                              <label htmlFor="pf-select-all" className="ml-2">Select All</label>
                            </div>
                            <small className="text-500" style={{ textAlign: 'center' }}>Min Dist.</small>
                            {/* nowrap + the wider grid column above: at 100px the label wrapped
                                and left the info icon stranded on a second line. */}
                            <small className="text-500" style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                              Direct Rebate
                              <i
                                className="pi pi-info-circle direct-rebate-info-icon ml-1"
                                data-pr-tooltip="A flat-dollar rebate earned on 1 equivalent-unit dose that is paid directly, outside of any contract opportunity. This rebate is added on top of contract rebates when calculating net cost recovery."
                                data-pr-position="top"
                                style={{ fontSize: '0.75rem', cursor: 'pointer', verticalAlign: 'middle' }}
                              />
                              <Tooltip target=".direct-rebate-info-icon" position="top" className="admin-fee-tooltip" />
                            </small>
                            <small className="text-500" style={{ textAlign: 'center' }}>
                              Admin Fee
                              <i
                                className="pi pi-info-circle admin-fee-info-icon ml-1"
                                data-pr-tooltip="A flat-dollar estimate of the reimbursement, net of cost, associated with administering of 1 equivalent-unit dose to the patient. This fee is added on top of the drug reimbursement when calculating net cost recovery."
                                data-pr-position="top"
                                style={{ fontSize: '0.75rem', cursor: 'pointer', verticalAlign: 'middle' }}
                              />
                              <Tooltip target=".admin-fee-info-icon" position="top" className="admin-fee-tooltip" />
                            </small>
                          </div>
                          {activeCategoryFamilies.map(pf => (
                            <React.Fragment key={pf.product_family_id}>
                              <div className="flex align-items-center">
                                <Checkbox
                                  inputId={`pf-${pf.product_family_id}`}
                                  checked={selectedProductFamilies[pf.product_family_name]?.include ?? false}
                                  onChange={(e) => {
                                    const updated = {
                                      ...selectedProductFamilies,
                                      [pf.product_family_name]: {
                                        ...(selectedProductFamilies[pf.product_family_name] || { minDistribution: 0 }),
                                        include: e.checked ?? false,
                                      },
                                    };
                                    dispatch(scenarioActions.setGeneratorState({ selectedFamilies: updated }));
                                    pruneDisabledExcludedOpportunities(updated);
                                  }}
                                />
                                <label htmlFor={`pf-${pf.product_family_id}`} className="ml-2">{pf.product_family_name}</label>
                              </div>
                              <InputNumber
                                value={Math.round(((selectedProductFamilies[pf.product_family_name]?.minDistribution ?? 0) * 100))}
                                onValueChange={(e) => {
                                  const pct = typeof e.value === 'number' ? e.value : 0;
                                  const clamped = Math.max(0, Math.min(100, Math.round(pct)));
                                  const asFraction = clamped / 100;
                                  const roundedFraction = Math.round(asFraction * 100) / 100;
                                  const updated = {
                                    ...selectedProductFamilies,
                                    [pf.product_family_name]: {
                                      ...(selectedProductFamilies[pf.product_family_name] || {}),
                                      minDistribution: roundedFraction,
                                    },
                                  };
                                  dispatch(scenarioActions.setGeneratorState({ selectedFamilies: updated }));
                                }}
                                min={0}
                                max={100}
                                step={1}
                                suffix="%"
                                useGrouping={false}
                                minFractionDigits={0}
                                maxFractionDigits={0}
                                style={{ width: '90px' }}
                                placeholder='0%'
                                disabled={!selectedProductFamilies[pf.product_family_name]?.include}
                              />
                              {/* Direct rebate: positive-only, so min is 0 rather than the
                                  admin fee's -9999 (a negative admin fee is a real net cost). */}
                              <InputNumber
                                value={selectedProductFamilies[pf.product_family_name]?.directRebatePerEu ?? null}
                                onValueChange={(e) => {
                                  const raw = typeof e.value === 'number' ? e.value : 0;
                                  const updated = {
                                    ...selectedProductFamilies,
                                    [pf.product_family_name]: {
                                      ...(selectedProductFamilies[pf.product_family_name] || {}),
                                      directRebatePerEu: Math.max(0, raw),
                                    },
                                  };
                                  dispatch(scenarioActions.setGeneratorState({ selectedFamilies: updated }));
                                }}
                                min={0}
                                max={9999}
                                step={1}
                                mode="currency"
                                currency="USD"
                                locale="en-US"
                                minFractionDigits={0}
                                maxFractionDigits={2}
                                style={{ width: '100%' }}
                                placeholder='$0'
                                onKeyDown={blockCurrencySymbolBackspace}
                                disabled={!selectedProductFamilies[pf.product_family_name]?.include}
                              />
                              <InputNumber
                                value={selectedProductFamilies[pf.product_family_name]?.adminFeePerEu ?? null}
                                onValueChange={(e) => {
                                  const updated = {
                                    ...selectedProductFamilies,
                                    [pf.product_family_name]: {
                                      ...(selectedProductFamilies[pf.product_family_name] || {}),
                                      adminFeePerEu: typeof e.value === 'number' ? e.value : 0,
                                    },
                                  };
                                  dispatch(scenarioActions.setGeneratorState({ selectedFamilies: updated }));
                                }}
                                min={-9999}
                                max={9999}
                                step={1}
                                mode="currency"
                                currency="USD"
                                locale="en-US"
                                minFractionDigits={0}
                                maxFractionDigits={2}
                                style={{ width: '100px' }}
                                placeholder='$0'
                                onKeyDown={blockCurrencySymbolBackspace}
                                disabled={!selectedProductFamilies[pf.product_family_name]?.include}
                              />
                            </React.Fragment>
                          ))}
                          {activeCategory && (
                            <div style={{ gridColumn: '1 / -1', marginTop: '0.75rem' }}>
                              <CategorySizeControl
                                key={activeCategory}
                                category={activeCategory}
                                factor={categoryScaleFactors[activeCategory] ?? 1.0}
                                baselineUnits={activeCategoryBaselineUnits}
                                onChange={(factor) => handleCategoryScaleChange(activeCategory, factor)}
                              />
                            </div>
                          )}
                          {!generateByPayers && (
                            <React.Fragment>
                              <div style={{ gridColumn: '1 / -1', height: '0.5rem' }} />
                              <div style={{ gridColumn: '1 / -1' }}>
                                <label htmlFor="maxScenarios" className="font-medium mb-2 block">Max Scenarios</label>
                                <InputNumber
                                  id="maxScenarios"
                                  value={formData.max_scenarios ?? 25}
                                  onValueChange={(e) => handleInputChange('max_scenarios', e.value)}
                                  min={1}
                                  max={50}
                                  placeholder='0'
                                  className={`w-full ${validationErrors.max_scenarios ? 'p-invalid' : ''}`}
                                  inputStyle={{ width: '100%' }}
                                  style={{ width: '100%' }}
                                />
                                {validationErrors.max_scenarios && (
                                  <small className="p-error block mt-1">{validationErrors.max_scenarios}</small>
                                )}
                              </div>
                            </React.Fragment>
                          )}
                        </div>
                      ) : (
                        <div style={{ paddingRight: '0.5rem' }}>
                          <small className="text-500">No product families to configure yet.</small>
                        </div>
                      )}
                    </div>
                  ) : (
                    <small className="text-500">Select at least one Competitive Therapeutic Area (CTA) to configure product families</small>
                  )}

                  <div className="flex flex-wrap gap-2 mt-3">
                  {/* The oral-inventory hint hangs off this span. It used to use
                      tooltipOptions.showOnDisabled, which re-parents the button into a
                      raw wrapper div outside React; this button unmounts via `visible`,
                      and React then removes it from the wrong parent, taking down the
                      app with a removeChild NotFoundError. */}
                  <Tooltip target=".payer-settings-oral-tip" position="top" />
                  <span
                    className={`inline-flex${hasOralInventory ? ' payer-settings-oral-tip' : ''}`}
                    data-pr-tooltip={hasOralInventory ? 'Payer settings are not applicable for oral inventory categories.' : undefined}
                  >
                  <Button
                    label={!payerMixLoaded
                      ? "Loading Payer Settings..."
                      : payerMixEligible
                        ? "Configure Payer Settings"
                        : "No Payer Data Available"}

                    icon={!payerMixLoaded
                      ? "pi pi-spin pi-spinner"
                      : payerMixEligible
                        ? "pi pi-sliders-h"
                        : "pi pi-info-circle"}
                    className="mr-3 generate-button"
                    visible={!!(formData.categories && formData.categories.length > 0)}
                    disabled={!payerMixLoaded || !payerMixEligible || !matrixReady || hasOralInventory}
                    onClick={() => {
                      if (!payerMixLoaded || !payerMixEligible || !matrixReady || hasOralInventory) return;
                      originalMatrixRef.current = JSON.stringify(drugPayerMatrix);
                      setMatrixDirty(false);
                      setShowEligibilityMatrix(true);
                    }}
                    style={{maxWidth: '300px'}}
                  />
                  </span>

                  <Button
                    label={excludedOpportunities ? "Excluded Opportunities" : "Loading Excluded Opportunities..."}
                    icon={excludedOpportunities ? "pi pi-file" : "pi pi-spin pi-spinner"}
                    className="generate-button"
                    onClick={() => {
                      originalExcludedOpportunitiesRef.current = [...selectedExcludedOpportunities];
                      setShowExcludedOpportunities(true);
                    }}
                    disabled={excludedOpportunities.length === 0 || !excludedOpportunities.some((opp) => opp.category === activeCategory)}
                    visible={!!(formData.categories && formData.categories.length > 0)}
                    style={{maxWidth: '300px'}}

                  />
                  </div>

                </AccordionTab>
              </Accordion>

              <Divider />

              {/* Generation Progress // REMOVED SINCE TEXT LOOKS SUPER BASIC AND UNNECESSARY WITH THE SPINNER
              {isGenerating && (
                <div className="field">
                  <small className="text-500 mt-1 block">
                    Generating scenarios and calculating results...
                  </small>
                </div>
              )} */}

              {/* Generate Button */}
              {(() => {
                // Check if at least one category has >=2 product families with >0% max market share
                const hasAnyEligibleCategory = formData.categories && formData.categories.some(cat => {
                  const families = productFamiliesByCategory[cat] || [];
                  const catMix = payerMixByCategory[cat] ?? [];
                  const eligibleFamilies = families.filter(pf => {
                    const selection = selectedProductFamilies[pf.product_family_name];
                    if (!selection?.include) return false;
                    if (catMix.length === 0) return true; // No payer mix data — count as eligible
                    const row = drugPayerMatrix[pf.product_family_id] || {};
                    const uncheckedPercent = catMix.reduce((sum, item) => {
                      const isChecked = row[item.payer] ?? true;
                      return !isChecked ? sum + item.percent : sum;
                    }, 0);
                    return (100 - uncheckedPercent) > 0;
                  });
                  return eligibleFamilies.length >= 2;
                });

                const generateDisabled =
                  isGenerating ||
                  !hasAnyEligibleCategory ||
                  (payerGroupingsLoading || (!matrixReady && payerMixEligible));

                return (
                  <Button
                    label={isGenerating ? 'Generating Scenarios...' : 'Generate Scenarios'}
                    icon={isGenerating ? 'pi pi-spin pi-spinner' : 'pi pi-bolt'}
                    onClick={() => void handleGenerateScenarios(generateByPayers ? 'payer' : 'legacy')}
                    disabled={generateDisabled}
                    className="w-full mt-1 generate-button"
                    size="large"
                  />
                );
              })()}
            </div>
          </Card>
        </div>

        {/* Product Families Preview */}
        <div className="col-12 lg:col-6">
            <Tooltip target=".pi-info-circle" />
            <Card
              title={
              <span>
                Available Product Families{' '}
                <i
                className="pi pi-info-circle"
                data-pr-tooltip={
                  'Volume Mix:\n' +
                  'Unit-based distribution used for scenario generation\n\n' +
                  'Cost Mix:\n' +
                  'Spend allocation across drugs, does not affect scenarios'
                }
                data-pr-position="right"
                style={{
                  cursor: 'pointer',
                  marginLeft: '0.5rem',
                  color: '#6c757d',
                  fontSize: '1rem'
                }}
                />
              </span>
              }
              className=""
            >
            {formData.categories && formData.categories.length > 0 ? (
              <div>
                <div className="scroll-tabs mb-2">
                  {formData.categories.map(cat => (
                    <button
                      key={cat}
                      type="button"
                      className={`scroll-tab ${activeCategory === cat ? 'active' : ''}`}
                      onClick={() => setActiveCategory(cat)}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
                <div>
                  {(activeCategory && productFamiliesByCategory[activeCategory]?.length) ? (
                    <DataTable
                      value={activeCategoryFamilies}
                      size="small"
                      className="p-datatable-sm"
                      emptyMessage="No product families available"
                      removableSort
                    >
                      <Column field="product_family_name" header="Product Family" sortable style={{ width: '40%' }} />
                      <Column field='display_unit_equivalency' header='Unit Equivalence' sortable style={{ width: '15%' }} />
                      <Column field="current_distribution" header="Volume Mix" body={distributionBodyTemplate} sortable style={{ width: '25%' }} />
                      <Column field="price_distribution" header="Cost Mix" body={distributionSpendBodyTemplate} sortable sortField="current_spend_distribution" style={{ width: '25%' }} />
                      <Column field="is_active" header="Status" body={statusBodyTemplate} sortable sortField="current_distribution" style={{ width: '10%' }} />
                    </DataTable>
                  ) : (
                    <div className="text-center p-4">
                      <i className="pi pi-info-circle text-4xl text-300 mb-3"></i>
                      <p className="text-500 mb-3">No product families found for this category</p>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-center p-4">
                <i className="pi pi-info-circle text-4xl text-300 mb-3"></i>
                <p className="text-500 mb-3">
                  Please select an organization and at least one CTA to view available product families
                </p>
              </div>
            )}
          </Card>

          {activeCategory && (
            <Card title={`Excluded Opportunities (${activeCategory})`} className="mt-3">
              {selectedExcludedOpportunities.filter(opp => opp.category === activeCategory).length > 0 ? (
                <>
                <Tooltip target=".applied-excl-opp-cell" />
                <DataTable
                  value={selectedExcludedOpportunities.filter(opp => opp.category === activeCategory)}
                  size="small"
                  className="p-datatable-sm"
                  emptyMessage="No excluded opportunities selected for this competitive therapeutic area."
                  dataKey="unique_id"
                >
                  <Column field="opportunity" header="Opportunity" body={(rowData: ExcludedOpportunity) => (
                    <span
                      className="applied-excl-opp-cell"
                      data-pr-tooltip={rowData.exclusion_reason?.trim() || undefined}
                      style={{ display: 'block' }}
                    >
                      {rowData.opportunity}
                    </span>
                  )} />
                  <Column field="tier" header="Tier" />
                  <Column field="rebate" header="Rebate" body={(rowData: ExcludedOpportunity) => {
                    if (rowData.rebate == null) return '-';
                    const rebateNum = Number(rowData.rebate);
                    if (isNaN(rebateNum)) return '-';
                    return rowData.paid_on?.toLocaleLowerCase().trim() === '$ per unit' 
                      ? `$${rebateNum.toFixed(2)}` 
                      : `${(rebateNum * 100).toFixed(2)}%`;
                  }} />
                </DataTable>
                </>
              ) : (
                <p className="text-500 m-0">No Excluded Rebate Opportunities Applied for {activeCategory}</p>
              )}
            </Card>
          )}

        </div>
      </div>
      <Dialog
        header="Payer Eligibility"
        visible={showEligibilityMatrix}
        style={{ width: "90vw" }}
        className="payer-eligibility-dialog"
        modal
        dismissableMask={false}
        onHide={() => {
          // If the dialog is closed without saving, revert to original matrix snapshot
          try {
            const original = JSON.parse(originalMatrixRef.current || '{}');
            const revertedMaxShares = computeDrugMaxShares(original, eligibilityFamilies);
            dispatch(
              scenarioActions.setGeneratorState({
                form: { ...formData, drugPayerMatrix: original, drugMaxShares: revertedMaxShares },
              })
            );
            setDrugPayerMatrix(original);
            setMatrixDirty(false);
          } catch (e) {
            // Fallback to current full matrix if parsing fails
            const full = buildFullDrugPayerMatrix();
            dispatch(scenarioActions.setGeneratorState({ form: { ...formData, drugPayerMatrix: full } }));
            setDrugPayerMatrix(full);
          }
          setShowEligibilityMatrix(false);
        }}
      >
        <TabView
          activeIndex={payerDialogActiveTabIndex}
          onTabChange={(e) => setPayerDialogActiveTabIndex(e.index)}
        >
          <TabPanel header="Eligibility Matrix">
            <Tooltip target=".payer-grouping-link" />
            <div className="effective-share-toggle">
              <InputSwitch
                inputId="toggle-effective-share"
                className="effective-share-switch"
                checked={showEffectiveShare}
                onChange={(e) => setShowEffectiveShare(e.value)}
              />
              <label htmlFor="toggle-effective-share" className="effective-share-toggle-label">
                Show effective share
              </label>
              <Tooltip target=".effective-share-info-icon" position="right" className="effective-share-tooltip" />
              <i
                className="pi pi-info-circle effective-share-info-icon"
                data-pr-tooltip={"The payer's share of a given product's market opportunity, taking into consideration each payer's formulary restrictions."}
                style={{ fontSize: '0.85rem', cursor: 'pointer', color: '#173A63', verticalAlign: 'middle' }}
              />
            </div>
            <div className="overflow-auto max-h-[60vh]">
          <table className="min-w-full border border-gray-300 rounded-lg">
            <thead className="bg-gray-100">
              {/* --- Row 1: Payer Percentages --- */}
              <tr>
                <th className="border border-gray-300 px-4 py-2" style={{ position: 'sticky', left: 0, backgroundColor: '#f3f4f6', zIndex: 20, minWidth: '120px' }}></th>
                <th className="border border-gray-300 px-4 py-2" style={{ position: 'sticky', left: '120px', backgroundColor: '#f3f4f6', zIndex: 20, minWidth: '80px' }}></th>
                <th className="border border-gray-300 px-4 py-2" style={{ position: 'sticky', left: '200px', backgroundColor: '#f3f4f6', zIndex: 20, minWidth: '180px' }}></th>
                {visiblePayerMix.map((item) => (
                  <th
                    key={`${item.payer}-percent`}
                    className="border border-gray-300 px-4 py-1 text-center text-xs text-gray-500 font-medium"
                  >
                    {item.percent.toFixed(1)}%
                  </th>
                ))}
              </tr>

              {/* --- Row 2: Payer Names --- */}
              <tr>
                <th className="border border-gray-300 px-4 py-2 text-left text-sm font-semibold text-gray-700" style={{ position: 'sticky', left: 0, backgroundColor: '#f3f4f6', zIndex: 20, minWidth: '120px' }}>
                  Max Market Share
                </th>
                <th className="border border-gray-300 px-4 py-2 text-center text-sm font-semibold text-gray-700" style={{ position: 'sticky', left: '120px', backgroundColor: '#f3f4f6', zIndex: 20, minWidth: '80px' }}>
                  <div className="flex flex-column align-items-center gap-1">
                    <span>Select All</span>
                    <Checkbox
                      inputId="matrix-select-all"
                      checked={matrixReady ? isEntireMatrixSelected : false}
                      disabled={!matrixReady}
                      onChange={(e) => matrixReady && toggleAllMatrix(e.checked ?? false)}
                    />
                  </div>
                </th>
                <th className="border border-gray-300 px-4 py-2 text-left text-sm font-semibold text-gray-700" style={{ position: 'sticky', left: '200px', backgroundColor: '#f3f4f6', zIndex: 20, minWidth: '180px' }}>
                  Product Family
                </th>
                {visiblePayerMix.map((item) => {
                  const hasGrouping = payersInGroupings.has(item.payer.trim().toLowerCase());
                  return (
                    <th
                      key={item.payer}
                      className="border border-gray-300 px-4 py-2 text-center text-sm font-semibold text-gray-700"
                    >
                      <button
                        type="button"
                        className={hasGrouping ? 'payer-grouping-link' : 'payer-grouping-link payer-grouping-link-disabled'}
                        onClick={() => hasGrouping && jumpToPayerGrouping(item.payer)}
                        data-pr-tooltip={hasGrouping ? 'Jump to payer grouping' : 'No payer grouping available'}
                        data-pr-position="top"
                        disabled={!hasGrouping}
                      >
                        {item.payer}
                      </button>
                    </th>
                  );
                })}
              </tr>
            </thead>

            <tbody>
              {eligibilityFamilies.map((pf, idx) => {
                const uncheckedPercent = visiblePayerMix.reduce((acc, item) => {
                  const isChecked =
                    drugPayerMatrix[pf.product_family_id]?.[item.payer] ?? true;
                  return !isChecked ? acc + item.percent : acc;
                }, 0);

                // Clamp to [0, 100] and round to one decimal
                const maxMarketShare = Math.max(0, Math.min(100, 100 - uncheckedPercent));

                const isSelected =
                  selectedProductFamilies[pf.product_family_name]?.include ?? true;

                const isRowAllSelected = visiblePayerMix.every((item) =>
                  (drugPayerMatrix[pf.product_family_id]?.[item.payer] ?? true)
                );

                return (
                  <tr
                    key={pf.product_family_id}
                    className={`${idx % 2 === 0 ? "bg-white" : "bg-gray-50"
                      } ${!isSelected ? "opacity-40 pointer-events-none" : ""}`}
                  >
                    <td className="border border-gray-300 px-4 py-2 text-center font-medium" style={{ position: 'sticky', left: 0, backgroundColor: idx % 2 === 0 ? '#ffffff' : '#f9fafb', zIndex: 10, minWidth: '120px', color: '#173A63' }}>
                      {maxMarketShare <= 0.0 ? "0.0" : (Math.round(maxMarketShare * 10) / 10).toFixed(1)}%
                    </td>
                    <td className="border border-gray-300 px-4 py-2 text-center" style={{ position: 'sticky', left: '120px', backgroundColor: idx % 2 === 0 ? '#ffffff' : '#f9fafb', zIndex: 10, minWidth: '80px' }}>
                      <Checkbox
                        inputId={`rowall-${pf.product_family_id}`}
                        checked={matrixReady ? isRowAllSelected : false}
                        disabled={!isSelected || !matrixReady}
                        onChange={(e) => matrixReady && toggleRowAll(pf.product_family_id, e.checked ?? false)}
                      />
                    </td>
                    <td className="border border-gray-300 px-4 py-2 font-medium text-gray-800" style={{ position: 'sticky', left: '200px', backgroundColor: idx % 2 === 0 ? '#ffffff' : '#f9fafb', zIndex: 10, minWidth: '180px' }}>
                      {pf.product_family_name.trim()}
                    </td>
                    {visiblePayerMix.map((item) => {
                      const isCellChecked = drugPayerMatrix[pf.product_family_id]?.[item.payer] ?? true;
                      const effectiveShare = maxMarketShare > 0 ? (item.percent / maxMarketShare) * 100 : 0;
                      return (
                        <td
                          key={`${pf.product_family_id}-${item.payer}`}
                          className="border border-gray-300 px-4 py-2 text-center"
                        >
                          <div className="flex flex-column align-items-center gap-1">
                            <Checkbox
                              inputId={`chk-${pf.product_family_id}-${item.payer}`}
                              checked={matrixReady ? isCellChecked : false}
                              disabled={!isSelected || !matrixReady}
                              onChange={(e) => {
                                if (!matrixReady) return;
                                setDrugPayerMatrix((prev) => {
                                  const prevVal = prev[pf.product_family_id]?.[item.payer];
                                  const nextVal = e.checked ?? false;
                                  if (prevVal !== nextVal) setMatrixDirty(true);
                                  return {
                                    ...prev,
                                    [pf.product_family_id]: {
                                      ...(prev[pf.product_family_id] || {}),
                                      [item.payer]: nextVal,
                                    },
                                  };
                                });
                              }}
                            />
                            {showEffectiveShare && matrixReady && isCellChecked && maxMarketShare > 0 && (
                              <span className="effective-share-value">
                                {effectiveShare.toFixed(1)}%
                              </span>
                            )}
                          </div>
                        </td>
                      );
                    })}

                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="flex justify-end gap-2 mt-4">
          <Button
            label="Cancel"
            className="p-button-text"
            onClick={() => {
              // Explicit cancel should revert any unsaved changes
              try {
                const original = JSON.parse(originalMatrixRef.current || '{}');
                const revertedMaxShares = computeDrugMaxShares(original, eligibilityFamilies);
                dispatch(
                  scenarioActions.setGeneratorState({
                    form: { ...formData, drugPayerMatrix: original, drugMaxShares: revertedMaxShares },
                  })
                );
                setDrugPayerMatrix(original);
                setMatrixDirty(false);
              } catch (e) {
                const full = buildFullDrugPayerMatrix();
                dispatch(scenarioActions.setGeneratorState({ form: { ...formData, drugPayerMatrix: full } }));
                setDrugPayerMatrix(full);
              }
              setShowEligibilityMatrix(false);
            }}
          />
          <Button
            label="Save"
            disabled={!matrixDirty}
            onClick={() => {
              if (!matrixDirty) return;
              // Compute max shares before closing dialog
              const drugMaxShares = eligibilityFamilies.map((pf) => {
                const uncheckedPercent = payerMix.reduce((acc, item) => {
                  const isChecked = drugPayerMatrix[pf.product_family_id]?.[item.payer] ?? true;
                  return !isChecked ? acc + item.percent : acc;
                }, 0);
                // Clamp to [0, 100] and round to one decimal
                const maxMarketShare = Math.max(0, Math.min(100, 100 - uncheckedPercent));
                return {
                  productFamilyId: pf.product_family_id,
                  productFamilyName: pf.product_family_name.trim(),
                  maxMarketShare: Math.round(maxMarketShare * 10) / 10,
                };
              });
              const fullMatrix = buildFullDrugPayerMatrix();
              const newMaxShares = drugMaxShares.reduce<Record<string, number>>((acc, d) => {
                acc[d.productFamilyId] = d.maxMarketShare;
                return acc;
              }, {});

              dispatch(scenarioActions.setGeneratorState({
                form: {
                  ...formData,
                  drugPayerMatrix: fullMatrix,
                  drugMaxShares: newMaxShares,
                }
              }));
              // Do NOT overwrite originalMatrixRef here; keep the pre-edit snapshot
              setMatrixDirty(false);
              // Close immediately
              setShowEligibilityMatrix(false);
              toast.current?.show(
                isDemoMode
                  ? {
                      severity: 'info',
                      summary: 'Applied for this session',
                      detail: 'Read-only demo account — eligibility preferences were not saved.',
                      life: 3000,
                    }
                  : {
                      severity: 'info',
                      summary: 'Saving',
                      detail: 'Saving payer eligibility preferences...',
                      life: 1500,
                    }
              );
              // Fire-and-forget save. Demo accounts are read-only server-side (423),
              // but the edited matrix still applies to this session so scenarios can
              // be modeled with it — it just isn't persisted.
              if (formData.logged_in_user && !isDemoMode) {
                ApiService.saveDrugPayerMatrix(formData.logged_in_user, fullMatrix)
                  .then(() => {
                    toast.current?.show({
                      severity: 'success',
                      summary: 'Saved',
                      detail: 'Payer eligibility preferences saved.',
                      life: 2500,
                    });
                  })
                  .catch((err) => {
                    console.error('Failed to save drug payer matrix', err);
                    toast.current?.show({
                      severity: 'error',
                      summary: 'Save Failed',
                      detail: 'Error saving. Using current defaults.',
                      life: 3500,
                    });
                    // Revert UI state to original matrix and recompute max shares
                    try {
                      const original = JSON.parse(originalMatrixRef.current || '{}');
                      const revertedMaxShares = eligibilityFamilies.map((pf) => {
                        const uncheckedPercent = payerMix.reduce((acc, item) => {
                          const isChecked = original[pf.product_family_id]?.[item.payer] ?? true;
                          return !isChecked ? acc + item.percent : acc;
                        }, 0);
                        const maxMarketShare = 100 - uncheckedPercent;
                        return {
                          productFamilyId: pf.product_family_id,
                          maxMarketShare,
                        };
                      }).reduce<Record<string, number>>((acc, d) => {
                        acc[d.productFamilyId] = d.maxMarketShare;
                        return acc;
                      }, {});

                      dispatch(scenarioActions.setGeneratorState({
                        form: {
                          ...formData,
                          drugPayerMatrix: original,
                          drugMaxShares: revertedMaxShares,
                        }
                      }));
                      // Also revert the local matrix state for the dialog
                      setDrugPayerMatrix(original);

                    } catch (e) {
                      console.warn('Failed to revert to original matrix after save error', e);
                    }
                  });
              }
            }}
          />
        </div>
          </TabPanel>
          {(!payerGroupingsLoading && payerGroupings.length > 0) && (
            <TabPanel header="Payer Groupings">
              <div style={{ maxHeight: '55vh', overflowY: 'auto' }}>
                <DataTable
                  value={groupedPayerRows}
                  paginator
                  rows={payerGroupingsRowsPerPage}
                  first={payerGroupingFirst}
                  onPage={(e) => setPayerGroupingFirst(e.first)}
                  responsiveLayout="scroll"
                  className="payer-groupings-table"
                  rowClassName={(rowData: any) => {
                    const classes: string[] = [rowData?.isGroupEnd ? 'payer-group-end' : 'payer-group-mid'];
                    if (payerGroupingHighlight && rowData?.masterPayorGroupFull === payerGroupingHighlight) {
                      classes.push('payer-group-highlight');
                    }
                    return classes.join(' ');
                  }}
                >
                  <Column field="masterPayorGroup" header="Master Payor Group" />
                  <Column
                    field="insurancePayerPrimary"
                    header="Insurance Payer Primary"
                    body={(rowData: any) => (
                      <span id={rowData.rowId}>{rowData.insurancePayerPrimary}</span>
                    )}
                  />
                </DataTable>
              </div>
              <div className="flex justify-end gap-2" style={{ position: 'sticky', bottom: 0, padding: '0.75rem 0 0', borderTop: '1px solid #e5e7eb', backgroundColor: '#fff', zIndex: 10 }}>
                <Button label="Close" onClick={() => setShowEligibilityMatrix(false)} />
              </div>
            </TabPanel>
          )}
        </TabView>
      </Dialog>

      <Dialog
        header="Excluded Opportunities"
        visible={showExcludedOpportunities}
        style={{ width: '95vw' }}
        dismissableMask={false}
        onHide={() => {
          setSelectedExcludedOpportunities(originalExcludedOpportunitiesRef.current);
          setShowExcludedOpportunities(false);
        }}
      >
        <Tooltip key={`excl-opp-tooltip-${selectedExcludedOpportunities.map(o => o.unique_id).join(',')}`} target=".excl-opp-cell" />
        <DataTable
            key={selectedExcludedOpportunities.map(o => o.unique_id).join(',')}
            value={[...(excludedOpportunities?.filter(opp =>
              activeCategory && opp.category === activeCategory
            ) || [])].sort((a, b) => {
              // Sort by contract_opportunities_id first, then by tier
              const idCompare = (a.contract_opportunities_id || 0) - (b.contract_opportunities_id || 0);
              if (idCompare !== 0) return idCompare;
              return (a.tier || 0) - (b.tier || 0);
            })}
            responsiveLayout="scroll"
            emptyMessage="No excluded opportunities found."
            selection={selectedExcludedOpportunities}
            onSelectionChange={(e) => setSelectedExcludedOpportunities(e.value as ExcludedOpportunity[])}
            dataKey="unique_id"
            selectionMode="checkbox"
            rowClassName={(data: ExcludedOpportunity) => ({
              'opacity-50 pointer-events-none': !isExcludedOpportunitySelectable(data)
            })}
        >
          <Column
            selectionMode="multiple"
            headerStyle={{ width: '3em' }}
            pt={{
              headerCheckbox: {
                root: { style: { display: 'none' } }
              }
            }}
          />
          <Column field="category" header="Competitive Therapeutic Area"
            body={(rowData: ExcludedOpportunity) => wrapWithOpportunityTooltip(rowData.category, rowData)} />
          <Column field="product_family_name" header="Product"
            body={(rowData: ExcludedOpportunity) => wrapWithOpportunityTooltip(rowData.product_family_name, rowData)} />
          <Column field="opportunity" header="Opportunity"
            body={(rowData: ExcludedOpportunity) => wrapWithOpportunityTooltip(rowData.opportunity, rowData)} />
          <Column field="trigger_type" header="Trigger Type"
            body={(rowData: ExcludedOpportunity) => wrapWithOpportunityTooltip(rowData.trigger_type, rowData)} />
          <Column field="co_start_date" header="Start Date" body={(rowData: ExcludedOpportunity) => {
            const content = rowData.co_start_date ? new Date(rowData.co_start_date).toLocaleDateString() : '';
            return wrapWithOpportunityTooltip(content, rowData);
          }} />
          <Column field="co_end_date" header="End Date" body={(rowData: ExcludedOpportunity) => {
            const content = rowData.co_end_date ? new Date(rowData.co_end_date).toLocaleDateString() : '';
            return wrapWithOpportunityTooltip(content, rowData);
          }} />
          <Column field="tier" header="Tier"
            body={(rowData: ExcludedOpportunity) => wrapWithOpportunityTooltip(rowData.tier, rowData)} />
          <Column field="rebate" header="Rebate" body={(rowData: ExcludedOpportunity) => {
            let content: React.ReactNode = '-';
            if (rowData.rebate != null) {
              const rebateNum = Number(rowData.rebate);
              if (!isNaN(rebateNum)) {
                content = rowData.paid_on?.toLocaleLowerCase().trim() === '$ per unit'
                  ? `$${rebateNum.toFixed(2)}`
                  : `${(rebateNum * 100).toFixed(2)}%`;
              }
            }
            return wrapWithOpportunityTooltip(content, rowData);
          }} />
          <Column field="paid_on" header="Paid On"
            body={(rowData: ExcludedOpportunity) => wrapWithOpportunityTooltip(rowData.paid_on, rowData)} />

        </DataTable>
        <div className="flex justify-end gap-2 mt-4">
          <Button
            label="Cancel"
            className="p-button-text"
            onClick={() => {
              setSelectedExcludedOpportunities(originalExcludedOpportunitiesRef.current);
              setShowExcludedOpportunities(false);
            }}
          />
          <Button
            label="Save"
            onClick={() => applyOpportunities(selectedExcludedOpportunities)}
          />
        </div>
      </Dialog>
    </div>
  );
};

export default ScenarioGenerator;
